import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import Toolbar from './Toolbar';
import DraggableItem from './DraggableItem';
import NoteComponent from './NoteComponent';
import TextComponent from './TextComponent';
import NotebookDocumentPage from './NotebookDocumentPage';
import MathComponent from './MathComponent';
import GraphComponent from './GraphComponent';
import FbdComponent from './FbdComponent';
import WorksheetElement from './WorksheetElement';
import ElementAnnotationLayer from './ElementAnnotationLayer';
import WorksheetUploadTray from './WorksheetUploadTray';
import WhiteboardInput from './WhiteboardInput';
import NeuroInformedBadge from '../NeuroInformedBadge';
import { DEFAULT_GRAPH_EXPRESSION, normalizeGraphExpressions } from './graphExpressions';
import {
  ToolType,
  ShapeType,
  ElementType,
  COLORS,
  NOTE_COLORS,
  generateId,
  createNoteElement,
  createTextElement,
  createMathElement,
  createChartElement,
  createFbdElement,
  createWorksheetElement,
  createShape
} from './types';

const ELEMENT_DIMENSIONS = {
  [ElementType.NOTE]: { width: 280, height: 260, minWidth: 220, minHeight: 180 },
  [ElementType.TEXT]: { width: 520, height: 180, minWidth: 260, minHeight: 100 },
  [ElementType.MATH]: { width: 640, height: 420, minWidth: 420, minHeight: 320 },
  [ElementType.CHART]: { width: 560, height: 420, minWidth: 420, minHeight: 300 },
  [ElementType.FBD]: { width: 420, height: 320, minWidth: 320, minHeight: 240 },
  [ElementType.WORKSHEET]: { width: 540, height: 720, minWidth: 260, minHeight: 220 },
};
const MINIMAP_WIDTH = 180;
const MINIMAP_HEIGHT = 120;
const MINIMAP_PADDING = 8;
const SOFT_PAN_MARGIN = 220;
const GRID_BASE_SPACING = 24;
const GRID_MIN_SPACING = 10;
const GRID_MAX_SPACING = 72;
const MAX_UPLOAD_SIZE_MB = 12;
const MAX_PDF_PAGES = 8;
const PDF_RENDER_SCALE = 1.25;
const PASTE_OFFSET = 20;
const SNAPSHOT_VERSION = 4;
const BOARD_MODES = {
  WHITEBOARD: 'whiteboard',
  NOTEBOOK: 'notebook',
};
const NOTEBOOK_PAGE_WIDTH = 816;
const NOTEBOOK_PAGE_HEIGHT = 1056;
const NOTEBOOK_PAGE_GAP = 72;
const NOTEBOOK_BACKGROUNDS = {
  LINED: 'lined',
  BLANK: 'blank',
  GRID: 'grid',
  DOCUMENT: 'document',
  IMPORTED: 'imported',
};
const PDF_WORKER_PATH = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const isEditableTarget = (target) => {
  if (!target) return false;
  if (target.isContentEditable) return true;
  if (typeof target.closest !== 'function') return false;
  return Boolean(target.closest('input, textarea, [contenteditable="true"], [contenteditable=""]'));
};

const cloneClipboardValue = (value) => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      // Fall back for any non-cloneable legacy values that may be in old snapshots.
    }
  }
  return JSON.parse(JSON.stringify(value));
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const plainTextToDocumentHtml = (value = '') => {
  const paragraphs = String(value).split('\n');
  return paragraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph) || '<br>'}</p>`)
    .join('');
};

const sanitizeDocumentHtml = (html = '') => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser !== 'function') {
    return plainTextToDocumentHtml(raw);
  }

  const allowedTags = new Set(['B', 'BR', 'DIV', 'EM', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'U', 'UL']);
  const allowedStyles = new Set(['color', 'font-family', 'font-size', 'font-style', 'font-weight', 'text-align', 'text-decoration']);
  const parser = new window.DOMParser();
  const parsed = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
  const cleanNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return document.createTextNode('');
    const tag = node.tagName.toUpperCase();
    const safeTag = allowedTags.has(tag) ? tag.toLowerCase() : 'span';
    const clean = document.createElement(safeTag);
    if (node instanceof HTMLElement && node.getAttribute('style')) {
      const safeStyles = [];
      node.getAttribute('style').split(';').forEach((entry) => {
        const [rawProp, ...rawValue] = entry.split(':');
        const prop = rawProp?.trim().toLowerCase();
        const value = rawValue.join(':').trim();
        if (!allowedStyles.has(prop) || !value) return;
        if (/[<>{}]/.test(value) || /url\s*\(/i.test(value) || /expression\s*\(/i.test(value)) return;
        safeStyles.push(`${prop}: ${value}`);
      });
      if (safeStyles.length) clean.setAttribute('style', safeStyles.join('; '));
    }
    Array.from(node.childNodes).forEach((child) => clean.appendChild(cleanNode(child)));
    return clean;
  };
  const wrapper = document.createElement('div');
  Array.from(parsed.body.firstElementChild?.childNodes || []).forEach((child) => wrapper.appendChild(cleanNode(child)));
  return wrapper.innerHTML;
};

const parseDocumentBlocksForPdf = (html = '') => {
  const safeHtml = sanitizeDocumentHtml(html);
  if (!safeHtml || typeof window === 'undefined' || typeof window.DOMParser !== 'function') return [];
  const parsed = new window.DOMParser().parseFromString(`<div>${safeHtml}</div>`, 'text/html');
  const blocks = [];

  const readStyle = (node, parentStyle = {}) => {
    const style = { ...parentStyle };
    if (node.nodeType !== Node.ELEMENT_NODE) return style;
    const tag = node.tagName.toUpperCase();
    if (tag === 'B' || tag === 'STRONG') style.bold = true;
    if (tag === 'I' || tag === 'EM') style.italic = true;
    if (tag === 'U') style.underline = true;
    const inline = node instanceof HTMLElement ? node.style : null;
    if (inline?.fontWeight && (inline.fontWeight === 'bold' || Number(inline.fontWeight) >= 600)) style.bold = true;
    if (inline?.fontStyle === 'italic') style.italic = true;
    if (inline?.textDecoration?.includes('underline')) style.underline = true;
    if (inline?.color) style.color = inline.color;
    if (inline?.fontSize) style.fontSize = inline.fontSize;
    if (inline?.fontFamily) style.fontFamily = inline.fontFamily;
    if (inline?.textAlign) style.align = inline.textAlign;
    return style;
  };

  const collectText = (node, style, parts) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent) parts.push({ text: node.textContent, style });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const nextStyle = readStyle(node, style);
    if (node.tagName?.toUpperCase() === 'BR') {
      parts.push({ text: '\n', style: nextStyle });
      return;
    }
    Array.from(node.childNodes).forEach((child) => collectText(child, nextStyle, parts));
  };

  const pushBlock = (node, listPrefix = '') => {
    const style = readStyle(node, {});
    const parts = [];
    collectText(node, style, parts);
    const text = `${listPrefix}${parts.map((part) => part.text).join('')}`.trimEnd();
    blocks.push({ text: text || ' ', style: parts[0]?.style || style });
  };

  Array.from(parsed.body.firstElementChild?.childNodes || []).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent?.trim()) blocks.push({ text: node.textContent, style: {} });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = node.tagName.toUpperCase();
    if (tag === 'UL' || tag === 'OL') {
      Array.from(node.children).forEach((child, index) => pushBlock(child, tag === 'OL' ? `${index + 1}. ` : '• '));
      return;
    }
    pushBlock(node);
  });
  return blocks;
};

const createDefaultNotebookPage = (title = 'Page 1', overrides = {}) => ({
  id: generateId(),
  title,
  backgroundType: NOTEBOOK_BACKGROUNDS.LINED,
  sourceAssetId: null,
  sourcePageNumber: null,
  backgroundSrc: null,
  documentContent: '',
  documentHtml: '',
  ...overrides,
});

const normalizeNotebook = (input) => {
  const pages = Array.isArray(input?.pages) && input.pages.length
    ? input.pages.map((page, index) => ({
      id: page?.id || generateId(),
      title: page?.title || `Page ${index + 1}`,
      backgroundType: page?.backgroundType || NOTEBOOK_BACKGROUNDS.LINED,
      sourceAssetId: page?.sourceAssetId || null,
      sourcePageNumber: page?.sourcePageNumber || null,
      backgroundSrc: page?.backgroundSrc || null,
      documentContent: typeof page?.documentContent === 'string' ? page.documentContent : '',
      documentHtml: sanitizeDocumentHtml(
        typeof page?.documentHtml === 'string' && page.documentHtml
          ? page.documentHtml
          : plainTextToDocumentHtml(page?.documentContent || '')
      ),
    }))
    : [createDefaultNotebookPage()];
  const activePageId = pages.some((page) => page.id === input?.activePageId)
    ? input.activePageId
    : pages[0].id;
  return { pages, activePageId };
};

const withNotebookPage = (entity, pageId) => (
  pageId ? { ...entity, notebookPageId: pageId } : entity
);

const belongsToNotebookPage = (entity, pageId) => (
  entity?.notebookPageId === pageId
);

const belongsToWhiteboard = (entity) => !entity?.notebookPageId;

const getNotebookPageRect = (index) => ({
  x: 0,
  y: index * (NOTEBOOK_PAGE_HEIGHT + NOTEBOOK_PAGE_GAP),
  width: NOTEBOOK_PAGE_WIDTH,
  height: NOTEBOOK_PAGE_HEIGHT,
});

const readImageDimensions = (src) => (
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    });
    image.onerror = () => resolve(null);
    image.src = src;
  })
);

const fitImageToWorksheetBox = (dimensions) => {
  const defaults = ELEMENT_DIMENSIONS[ElementType.WORKSHEET];
  if (!dimensions?.width || !dimensions?.height) {
    return { width: defaults.width, height: defaults.height };
  }

  const fitScale = Math.min(defaults.width / dimensions.width, defaults.height / dimensions.height, 1);
  const minScale = Math.min(
    defaults.width / dimensions.width,
    defaults.height / dimensions.height,
    Math.max(defaults.minWidth / dimensions.width, defaults.minHeight / dimensions.height, fitScale)
  );
  const scale = Math.max(fitScale, minScale);
  return {
    width: Math.round(dimensions.width * scale),
    height: Math.round(dimensions.height * scale),
  };
};

const cloneAnnotationsWithNewIds = (annotations) => {
  if (!Array.isArray(annotations)) return annotations;
  return annotations.map((annotation) => ({
    ...cloneClipboardValue(annotation),
    id: generateId(),
  }));
};

if (typeof window !== 'undefined' && pdfjsLib?.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_PATH;
}

const isPdfWorkerLoadError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return (
    message.includes('worker') ||
    message.includes('dynamically imported module') ||
    message.includes('setting up fake worker failed')
  );
};

const isPdfCompatibilityError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return (
    message.includes('tohex') ||
    message.includes('frombase64') ||
    message.includes('tobase64') ||
    message.includes('setfromhex') ||
    message.includes('setfrombase64')
  );
};

const loadPdfDocument = async (arrayBuffer) => {
  const primaryTask = pdfjsLib.getDocument({ data: arrayBuffer });
  try {
    return await primaryTask.promise;
  } catch (error) {
    primaryTask.destroy?.();
    if (!isPdfWorkerLoadError(error)) {
      throw error;
    }
    console.warn('PDF worker failed to load, retrying with disableWorker=true', error);
  }

  const fallbackTask = pdfjsLib.getDocument({ data: arrayBuffer, disableWorker: true });
  try {
    return await fallbackTask.promise;
  } catch (error) {
    fallbackTask.destroy?.();
    throw error;
  }
};

const normalizeElementDimensions = (element) => {
  const defaults = ELEMENT_DIMENSIONS[element?.type] || { width: 200, height: 120 };
  const minWidth = defaults.minWidth || defaults.width || 120;
  const minHeight = defaults.minHeight || defaults.height || 100;
  const rawWidth = typeof element?.width === 'number' ? element.width : defaults.width;
  const rawHeight = typeof element?.height === 'number' ? element.height : defaults.height;
  const width = Math.max(minWidth, rawWidth);
  const height = Math.max(minHeight, rawHeight);
  const normalized = normalizeEntityMeta({ ...element, width, height });
  if ((normalized?.type === ElementType.NOTE || normalized?.type === ElementType.CHART) && !Array.isArray(normalized.annotations)) {
    normalized.annotations = [];
  }
  if (normalized?.type === ElementType.CHART) {
    normalized.expressions = normalizeGraphExpressions(
      normalized.expressions,
      DEFAULT_GRAPH_EXPRESSION
    );
  }
  return normalized;
};

const isReactLikeEvent = (value) => (
  value &&
  typeof value === 'object' &&
  (
    typeof value.preventDefault === 'function' ||
    typeof value.stopPropagation === 'function' ||
    Object.prototype.hasOwnProperty.call(value, 'nativeEvent')
  )
);

const sanitizeOptionalText = (value) => {
  if (value == null || isReactLikeEvent(value)) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const safeHistoryStringify = (value) => {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, current) => {
    if (typeof current === 'function' || typeof current === 'symbol') return undefined;
    if (typeof current === 'bigint') return Number(current);
    if (current && typeof current === 'object') {
      if (isReactLikeEvent(current)) return undefined;
      if (typeof Element !== 'undefined' && current instanceof Element) return undefined;
      if (seen.has(current)) return undefined;
      seen.add(current);
    }
    return current;
  });
};

const normalizePoint = (point) => {
  if (!point || typeof point !== 'object') return null;
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
};

const normalizePath = (path) => {
  if (!path || typeof path !== 'object') return null;
  const rawPoints = Array.isArray(path.points) ? path.points : [];
  const points = rawPoints.map(normalizePoint).filter(Boolean);
  const width = Number(path.width);
  return {
    ...path,
    id: path.id || generateId(),
    tool: Object.values(ToolType).includes(path.tool) ? path.tool : ToolType.PEN,
    color: path.color || COLORS[0],
    width: Number.isFinite(width) && width > 0 ? width : 3,
    _updated_at: typeof path._updated_at === 'string' ? path._updated_at : null,
    _updated_by: typeof path._updated_by === 'string' ? path._updated_by : null,
    points,
  };
};

const normalizePaths = (input) => {
  if (!Array.isArray(input)) return [];
  return input.map(normalizePath).filter(Boolean);
};

const normalizeEntityMeta = (entity) => ({
  ...entity,
  _updated_at: typeof entity?._updated_at === 'string' ? entity._updated_at : null,
  _updated_by: typeof entity?._updated_by === 'string' ? entity._updated_by : null,
});

const normalizeShape = (shape) => normalizeEntityMeta({
  ...shape,
  id: shape?.id || generateId(),
});

const getPathBounds = (path) => {
  const points = path?.points || [];
  if (!points.length) return null;
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  points.forEach((p) => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  });
  return { minX, minY, maxX, maxY };
};

const isPathGroupResizable = (path) => path?.tool === ToolType.PEN || path?.tool === ToolType.MARKER;

const getGroupedPathBounds = (selectedIds, paths) => {
  const selectedSet = new Set(selectedIds);
  const bounds = paths
    .filter((path) => selectedSet.has(path.id))
    .map((path) => getPathBounds(path))
    .filter(Boolean);
  if (!bounds.length) return null;
  return {
    minX: Math.min(...bounds.map((b) => b.minX)),
    minY: Math.min(...bounds.map((b) => b.minY)),
    maxX: Math.max(...bounds.map((b) => b.maxX)),
    maxY: Math.max(...bounds.map((b) => b.maxY)),
  };
};

const getShapeBounds = (shape) => ({
  minX: Math.min(shape.x1, shape.x2),
  minY: Math.min(shape.y1, shape.y2),
  maxX: Math.max(shape.x1, shape.x2),
  maxY: Math.max(shape.y1, shape.y2),
});

const getElementBounds = (element) => ({
  minX: element.x,
  minY: element.y,
  maxX: element.x + (element.width || 0),
  maxY: element.y + (element.height || 0),
});

const boundsIntersect = (a, b) => (
  a.minX <= b.maxX &&
  a.maxX >= b.minX &&
  a.minY <= b.maxY &&
  a.maxY >= b.minY
);

const pointInBounds = (x, y, bounds) => (
  x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY
);

const distancePointToSegment = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    return Math.hypot(px - x1, py - y1);
  }
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.hypot(px - projX, py - projY);
};

const rotatePointAround = (point, center, angleDelta) => {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const cos = Math.cos(angleDelta);
  const sin = Math.sin(angleDelta);
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
};

const getTriangleVertices = (shape) => {
  const bounds = getShapeBounds(shape);
  return [
    { x: (bounds.minX + bounds.maxX) / 2, y: bounds.minY },
    { x: bounds.minX, y: bounds.maxY },
    { x: bounds.maxX, y: bounds.maxY },
  ];
};

const getInteriorAngleDeg = (vertex, pointA, pointB) => {
  const ax = pointA.x - vertex.x;
  const ay = pointA.y - vertex.y;
  const bx = pointB.x - vertex.x;
  const by = pointB.y - vertex.y;
  const magA = Math.hypot(ax, ay);
  const magB = Math.hypot(bx, by);
  if (!magA || !magB) return 0;
  const cosine = Math.max(-1, Math.min(1, (ax * bx + ay * by) / (magA * magB)));
  return Math.round(Math.acos(cosine) * 180 / Math.PI);
};

const getTriangleAngles = (vertices) => ([
  getInteriorAngleDeg(vertices[0], vertices[1], vertices[2]),
  getInteriorAngleDeg(vertices[1], vertices[0], vertices[2]),
  getInteriorAngleDeg(vertices[2], vertices[0], vertices[1]),
]);

const pointInTriangle = (point, vertices) => {
  const [a, b, c] = vertices;
  const sign = (p1, p2, p3) => (
    (p1.x - p3.x) * (p2.y - p3.y) -
    (p2.x - p3.x) * (p1.y - p3.y)
  );
  const d1 = sign(point, a, b);
  const d2 = sign(point, b, c);
  const d3 = sign(point, c, a);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
};

const segmentIntersectsBrush = (a1, a2, b1, b2, radius) => {
  const d1 = distancePointToSegment(a1.x, a1.y, b1.x, b1.y, b2.x, b2.y);
  const d2 = distancePointToSegment(a2.x, a2.y, b1.x, b1.y, b2.x, b2.y);
  const d3 = distancePointToSegment(b1.x, b1.y, a1.x, a1.y, a2.x, a2.y);
  const d4 = distancePointToSegment(b2.x, b2.y, a1.x, a1.y, a2.x, a2.y);
  return Math.min(d1, d2, d3, d4) <= radius;
};

const pathIntersectsEraserStroke = (path, eraserPoints, eraserRadius) => {
  const points = path?.points || [];
  if (points.length < 2 || eraserPoints.length < 2) return false;
  const strokeRadius = (path.width || 2) / 2;
  const radius = eraserRadius + strokeRadius;
  for (let i = 1; i < points.length; i += 1) {
    const a1 = points[i - 1];
    const a2 = points[i];
    for (let j = 1; j < eraserPoints.length; j += 1) {
      const b1 = eraserPoints[j - 1];
      const b2 = eraserPoints[j];
      if (segmentIntersectsBrush(a1, a2, b1, b2, radius)) {
        return true;
      }
    }
  }
  return false;
};

const shapeEdges = (shape) => {
  if (shape.type === ShapeType.TRIANGLE) {
    const vertices = getTriangleVertices(shape);
    return [
      [vertices[0], vertices[1]],
      [vertices[1], vertices[2]],
      [vertices[2], vertices[0]],
    ];
  }
  if (shape.type === ShapeType.RECTANGLE) {
    const left = Math.min(shape.x1, shape.x2);
    const right = Math.max(shape.x1, shape.x2);
    const top = Math.min(shape.y1, shape.y2);
    const bottom = Math.max(shape.y1, shape.y2);
    return [
      [{ x: left, y: top }, { x: right, y: top }],
      [{ x: right, y: top }, { x: right, y: bottom }],
      [{ x: right, y: bottom }, { x: left, y: bottom }],
      [{ x: left, y: bottom }, { x: left, y: top }],
    ];
  }
  if (shape.type === ShapeType.CIRCLE) {
    const cx = (shape.x1 + shape.x2) / 2;
    const cy = (shape.y1 + shape.y2) / 2;
    const rx = Math.max(2, Math.abs(shape.x2 - shape.x1) / 2);
    const ry = Math.max(2, Math.abs(shape.y2 - shape.y1) / 2);
    const edges = [];
    const segments = 24;
    let prev = { x: cx + rx, y: cy };
    for (let i = 1; i <= segments; i += 1) {
      const angle = (Math.PI * 2 * i) / segments;
      const next = { x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) };
      edges.push([prev, next]);
      prev = next;
    }
    return edges;
  }
  return [[{ x: shape.x1, y: shape.y1 }, { x: shape.x2, y: shape.y2 }]];
};

const shapeIntersectsEraserStroke = (shape, eraserPoints, eraserRadius) => {
  if (eraserPoints.length < 2) return false;
  const strokeRadius = (shape.strokeWidth || 3) / 2;
  const radius = eraserRadius + strokeRadius;
  const edges = shapeEdges(shape);
  for (let i = 1; i < eraserPoints.length; i += 1) {
    const b1 = eraserPoints[i - 1];
    const b2 = eraserPoints[i];
    for (const [a1, a2] of edges) {
      if (segmentIntersectsBrush(a1, a2, b1, b2, radius)) {
        return true;
      }
    }
  }
  return false;
};

const splitPathByEraserStroke = (path, eraserPoints, eraserRadius) => {
  const points = path?.points || [];
  if (points.length < 2 || eraserPoints.length < 2) {
    return null;
  }
  const strokeRadius = (path.width || 2) / 2;
  const radius = eraserRadius + strokeRadius;
  const segments = [];
  let current = [];
  let removedAny = false;

  for (let i = 1; i < points.length; i += 1) {
    const p1 = points[i - 1];
    const p2 = points[i];
    let erased = false;
    for (let j = 1; j < eraserPoints.length; j += 1) {
      const e1 = eraserPoints[j - 1];
      const e2 = eraserPoints[j];
      if (segmentIntersectsBrush(p1, p2, e1, e2, radius)) {
        erased = true;
        break;
      }
    }
    if (erased) {
      removedAny = true;
      if (current.length > 1) {
        segments.push(current);
      }
      current = [];
      continue;
    }
    if (current.length === 0) {
      current = [p1];
    }
    current.push(p2);
  }

  if (current.length > 1) {
    segments.push(current);
  }

  if (!removedAny) {
    return null;
  }

  return segments;
};

const normalizeTombstone = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const type = typeof entry.type === 'string' ? entry.type : '';
  const id = typeof entry.id === 'string' ? entry.id : '';
  if (!type || !id) return null;
  return {
    type,
    id,
    deleted_at: typeof entry.deleted_at === 'string' ? entry.deleted_at : new Date().toISOString(),
    deleted_by: typeof entry.deleted_by === 'string' ? entry.deleted_by : '',
  };
};

const dedupeTombstones = (entries) => {
  const byKey = new Map();
  (entries || []).forEach((entry) => {
    const normalized = normalizeTombstone(entry);
    if (!normalized) return;
    const key = `${normalized.type}:${normalized.id}`;
    const existing = byKey.get(key);
    if (!existing || normalized.deleted_at >= existing.deleted_at) {
      byKey.set(key, normalized);
    }
  });
  return Array.from(byKey.values())
    .sort((a, b) => (a.deleted_at < b.deleted_at ? 1 : -1))
    .slice(0, 600);
};

const migrateLegacyEraserPaths = (inputPaths) => {
  const normalized = normalizePaths(inputPaths);
  const eraserPaths = normalized.filter((path) => path.tool === ToolType.ERASER);
  if (!eraserPaths.length) {
    return normalized;
  }

  let workingPaths = normalized.filter((path) => path.tool !== ToolType.ERASER);
  eraserPaths.forEach((eraserPath) => {
    const eraserPoints = eraserPath.points || [];
    const eraserRadius = Math.max(4, (eraserPath.width || 40) / 2);
    const nextPaths = [];
    workingPaths.forEach((path) => {
      if (path.tool !== ToolType.PEN && path.tool !== ToolType.MARKER) {
        nextPaths.push(path);
        return;
      }
      const splitSegments = splitPathByEraserStroke(path, eraserPoints, eraserRadius);
      if (!splitSegments) {
        nextPaths.push(path);
        return;
      }
      splitSegments.forEach((segmentPoints) => {
        nextPaths.push(normalizePath({
          ...path,
          id: generateId(),
          points: segmentPoints,
          tool: path.tool,
        }));
      });
    });
    workingPaths = nextPaths;
  });
  return workingPaths;
};

// Get canvas coordinates from pointer/mouse/touch event
const getPos = (e, canvas) => {
  const rect = canvas.getBoundingClientRect();
  let clientX, clientY;

  if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
    clientX = e.clientX;
    clientY = e.clientY;
  } else if (e.touches && e.touches.length > 0) {
    clientX = e.touches[0].clientX;
    clientY = e.touches[0].clientY;
  } else {
    clientX = 0;
    clientY = 0;
  }

  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  };
};

const WhiteboardElementContent = React.memo(({
  element,
  isSelected,
  updateElement,
  currentTool,
  currentColor,
  focusedTextElementId,
  isAnnotating,
  onToggleAnnotating
}) => {
  const handleUpdate = useCallback((updates) => {
    updateElement(element.id, updates);
  }, [element.id, updateElement]);

  const handleToggleAnnotating = useCallback(() => {
    onToggleAnnotating?.(element.id);
  }, [element.id, onToggleAnnotating]);

  const annotationLayer = (
    <ElementAnnotationLayer
      annotations={element.annotations}
      currentTool={currentTool}
      currentColor={currentColor}
      isAnnotating={isAnnotating}
      onToggleAnnotating={handleToggleAnnotating}
      onUpdate={handleUpdate}
    />
  );

  if (process.env.NODE_ENV === 'development' && element.type === ElementType.CHART) {
    console.count('Whiteboard graph content render');
  }

  switch (element.type) {
    case ElementType.NOTE:
      return (
        <div className="wb-annotatable-element">
          <NoteComponent
            content={element.content}
            color={element.color}
            onUpdate={handleUpdate}
          />
          {annotationLayer}
        </div>
      );
    case ElementType.MATH:
      return (
        <MathComponent
          expression={element.expression}
          rawInput={element.rawInput}
          isEditing={element.isEditing}
          onUpdate={handleUpdate}
        />
      );
    case ElementType.TEXT:
      return (
        <TextComponent
          content={element.content}
          autoFocus={focusedTextElementId === element.id}
          onUpdate={handleUpdate}
        />
      );
    case ElementType.CHART:
      return (
        <div className="wb-annotatable-element">
          <GraphComponent
            expressions={element.expressions}
            isSelected={isSelected}
            onUpdate={handleUpdate}
          />
          {annotationLayer}
        </div>
      );
    case ElementType.FBD:
      return (
        <FbdComponent
          element={element}
          onUpdate={handleUpdate}
        />
      );
    case ElementType.WORKSHEET:
      return (
        <WorksheetElement
          element={element}
          currentTool={currentTool}
          currentColor={currentColor}
          isAnnotating={isAnnotating}
          onToggleAnnotating={handleToggleAnnotating}
          onUpdate={handleUpdate}
        />
      );
    default:
      return null;
  }
}, (prev, next) => {
  const prevEl = prev.element;
  const nextEl = next.element;
  if (prevEl?.id !== nextEl?.id || prevEl?.type !== nextEl?.type) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.updateElement !== next.updateElement) return false;
  if (prev.focusedTextElementId !== next.focusedTextElementId) return false;

  switch (nextEl.type) {
    case ElementType.NOTE:
      return (
        prev.currentTool === next.currentTool &&
        prev.currentColor === next.currentColor &&
        prev.isAnnotating === next.isAnnotating &&
        prevEl.content === nextEl.content &&
        prevEl.color === nextEl.color &&
        prevEl.annotations === nextEl.annotations
      );
    case ElementType.MATH:
      return (
        prevEl.expression === nextEl.expression &&
        prevEl.rawInput === nextEl.rawInput &&
        prevEl.isEditing === nextEl.isEditing
      );
    case ElementType.TEXT:
      return prevEl.content === nextEl.content;
    case ElementType.CHART:
      return (
        prev.currentTool === next.currentTool &&
        prev.currentColor === next.currentColor &&
        prev.isAnnotating === next.isAnnotating &&
        prevEl.expressions === nextEl.expressions &&
        prevEl.annotations === nextEl.annotations
      );
    case ElementType.FBD:
      return (
        prevEl.bodyLabel === nextEl.bodyLabel &&
        prevEl.forces === nextEl.forces &&
        prevEl.width === nextEl.width &&
        prevEl.height === nextEl.height
      );
    case ElementType.WORKSHEET:
      return (
        prev.currentTool === next.currentTool &&
        prev.currentColor === next.currentColor &&
        prev.isAnnotating === next.isAnnotating &&
        prev.onToggleAnnotating === next.onToggleAnnotating &&
        prevEl.assetId === nextEl.assetId &&
        prevEl.assetType === nextEl.assetType &&
        prevEl.src === nextEl.src &&
        prevEl.selectedPage === nextEl.selectedPage &&
        prevEl.pdfMeta === nextEl.pdfMeta &&
        prevEl.annotations === nextEl.annotations
      );
    default:
      return true;
  }
});

const WhiteboardCanvas = ({
  snapshot,
  syncClientId,
  onMount,
  onChange,
  onInteractionStateChange,
  sessionContext,
  injectedContext,
  onContextChange,
  currentPhase,
  isPublicShare = false,
  remoteCursors = [],
  onCursorChange
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const minimapCanvasRef = useRef(null);

  // Drawing state
  const [paths, setPaths] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Tool state
  const [currentTool, setCurrentTool] = useState(ToolType.PAN);
  const [currentColor, setCurrentColor] = useState(COLORS[0]);
  const [currentShapeType, setCurrentShapeType] = useState(ShapeType.RECTANGLE);
  const [eraserSize, setEraserSize] = useState(40);
  const [eraserMode, setEraserMode] = useState('normal');

  // Shape state
  const [shapes, setShapes] = useState([]);
  const [currentShape, setCurrentShape] = useState(null);
  const [shapeStart, setShapeStart] = useState(null);

  // Zoom/Pan state
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);

  // Elements state
  const [elements, setElements] = useState([]);
  const [tombstones, setTombstones] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [boardMode, setBoardMode] = useState(BOARD_MODES.WHITEBOARD);
  const [notebook, setNotebook] = useState(() => normalizeNotebook());
  const [uploadTrayExpanded, setUploadTrayExpanded] = useState(false);
  const [selectedUploadAssetId, setSelectedUploadAssetId] = useState(null);
  const [selectedUploadPage, setSelectedUploadPage] = useState(1);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [activeElementAnnotationId, setActiveElementAnnotationId] = useState(null);
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [focusedTextElementId, setFocusedTextElementId] = useState(null);
  const [selectedShapeIds, setSelectedShapeIds] = useState([]);
  const [selectedPathIds, setSelectedPathIds] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null);
  const [isSelectingBox, setIsSelectingBox] = useState(false);
  const [isMovingSelection, setIsMovingSelection] = useState(false);
  const [isResizingSelection, setIsResizingSelection] = useState(false);
  const [isRotatingSelection, setIsRotatingSelection] = useState(false);

  // Undo history state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoingRef = useRef(false);
  const suppressHistoryRef = useRef(false);
  const historyIndexRef = useRef(-1);
  const lastHistorySnapshotRef = useRef('');

  // Panning state (for click-and-drag)
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [spacePressed, setSpacePressed] = useState(false);
  const [stylusOnlyMode, setStylusOnlyMode] = useState(true);
  const activePointerRef = useRef({ id: null, type: null, pressureCapable: false });

  // AI message toast state
  const [aiMessage, setAiMessage] = useState(null);
  const [aiTechnique, setAiTechnique] = useState(null);
  const aiMessageTimeoutRef = useRef(null);
  const minimapDragRef = useRef({
    active: false,
    pointerId: null,
    offsetX: 0,
    offsetY: 0,
    startX: 0,
    startY: 0,
    dragged: false
  });
  const minimapDrawRafRef = useRef(null);
  const selectionDragRef = useRef(null);
  const internalClipboardRef = useRef(null);
  const pasteFallbackTimerRef = useRef(null);
  const pendingInternalPasteFallbackRef = useRef(false);
  const elementInteractionDepthRef = useRef(0);
  const insertionBurstRef = useRef({ timestamp: 0, count: 0 });
  const changeReasonRef = useRef(null);
  const activeStrokeRef = useRef(null);
  const strokeDrawRafRef = useRef(null);
  const strokeDrawQueueRef = useRef([]);
  const interactionEndRafRef = useRef(null);
  const localInteractionActiveRef = useRef(false);
  const pendingInteractionChangeRef = useRef(false);
  const pendingInteractionReasonRef = useRef('interaction-end');

  const showWhiteboardToast = useCallback((message, technique = null, duration = 5000) => {
    if (aiMessageTimeoutRef.current) {
      clearTimeout(aiMessageTimeoutRef.current);
    }
    setAiMessage(message);
    setAiTechnique(technique);
    aiMessageTimeoutRef.current = setTimeout(() => {
      setAiMessage(null);
      setAiTechnique(null);
    }, duration);
  }, []);

  const nowIso = useCallback(() => new Date().toISOString(), []);

  const markDiscreteChange = useCallback(() => {
    changeReasonRef.current = 'discrete';
  }, []);

  const beginLocalInteraction = useCallback(() => {
    localInteractionActiveRef.current = true;
    onInteractionStateChange?.(true);
  }, [onInteractionStateChange]);

  const markInteractionChanged = useCallback((reason = 'interaction-end') => {
    pendingInteractionChangeRef.current = true;
    pendingInteractionReasonRef.current = reason;
  }, []);

  const endLocalInteraction = useCallback((didMutate = false, reason = 'interaction-end') => {
    const shouldNotify = didMutate || pendingInteractionChangeRef.current;
    if (shouldNotify) {
      pendingInteractionChangeRef.current = false;
      pendingInteractionReasonRef.current = reason;
      if (interactionEndRafRef.current != null) {
        window.cancelAnimationFrame(interactionEndRafRef.current);
      }
      interactionEndRafRef.current = window.requestAnimationFrame(() => {
        interactionEndRafRef.current = null;
        onChange?.(reason);
        onInteractionStateChange?.(false);
        localInteractionActiveRef.current = false;
        pendingInteractionReasonRef.current = 'interaction-end';
      });
      return;
    }
    onInteractionStateChange?.(false);
    localInteractionActiveRef.current = false;
    pendingInteractionReasonRef.current = 'interaction-end';
  }, [onChange, onInteractionStateChange]);

  const stampUpdatedMeta = useCallback((entity) => normalizeEntityMeta({
    ...entity,
    _updated_at: nowIso(),
    _updated_by: syncClientId || '',
  }), [nowIso, syncClientId]);

  const buildTombstone = useCallback((type, id, deletedAt = null) => (
    normalizeTombstone({
      type,
      id,
      deleted_at: deletedAt || nowIso(),
      deleted_by: syncClientId || '',
    })
  ), [nowIso, syncClientId]);

  const appendTombstones = useCallback((entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return;
    setTombstones((prev) => dedupeTombstones([...prev, ...entries]));
  }, []);

  const emitLocalCursor = useCallback((pos, active = true, tool = currentTool) => {
    if (!pos || typeof onCursorChange !== 'function') return;
    onCursorChange({
      x: pos.x,
      y: pos.y,
      tool,
      active,
    });
  }, [currentTool, onCursorChange]);

  const isNotebookMode = boardMode === BOARD_MODES.NOTEBOOK;
  const activeNotebookPage = useMemo(() => {
    const normalized = normalizeNotebook(notebook);
    return normalized.pages.find((page) => page.id === normalized.activePageId) || normalized.pages[0];
  }, [notebook]);
  const activeNotebookPageId = activeNotebookPage?.id || null;
  const notebookPages = useMemo(() => normalizeNotebook(notebook).pages, [notebook]);
  const notebookPageRects = useMemo(() => (
    notebookPages.map((page, index) => ({
      ...getNotebookPageRect(index),
      page,
      pageId: page.id,
    }))
  ), [notebookPages]);
  const activeNotebookPageRect = useMemo(() => (
    notebookPageRects.find((rect) => rect.pageId === activeNotebookPageId) || notebookPageRects[0] || getNotebookPageRect(0)
  ), [activeNotebookPageId, notebookPageRects]);
  const getNotebookPageAtPoint = useCallback((point) => {
    if (!point) return null;
    return notebookPageRects.find((rect) => (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    )) || null;
  }, [notebookPageRects]);
  const isInsideActiveNotebookPage = useCallback((point) => (
    !isNotebookMode ||
    Boolean(getNotebookPageAtPoint(point))
  ), [getNotebookPageAtPoint, isNotebookMode]);
  const visiblePaths = useMemo(() => (
    isNotebookMode
      ? paths.filter((path) => notebookPages.some((page) => belongsToNotebookPage(path, page.id)))
      : paths.filter(belongsToWhiteboard)
  ), [isNotebookMode, notebookPages, paths]);
  const visibleShapes = useMemo(() => (
    isNotebookMode
      ? shapes.filter((shape) => notebookPages.some((page) => belongsToNotebookPage(shape, page.id)))
      : shapes.filter(belongsToWhiteboard)
  ), [isNotebookMode, notebookPages, shapes]);
  const visibleElements = useMemo(() => (
    isNotebookMode
      ? elements.filter((element) => notebookPages.some((page) => belongsToNotebookPage(element, page.id)))
      : elements.filter(belongsToWhiteboard)
  ), [elements, isNotebookMode, notebookPages]);

  // Load snapshot on mount
  useEffect(() => {
    if (snapshot && Number(snapshot.version || 0) >= 2) {
      setPaths(migrateLegacyEraserPaths(snapshot.paths));
      setElements((snapshot.elements || []).map(normalizeElementDimensions));
      setShapes((snapshot.shapes || []).map(normalizeShape));
      setTombstones(dedupeTombstones(snapshot._tombstones || []));
      setUploads(Array.isArray(snapshot.uploads) ? snapshot.uploads : []);
      setBoardMode(snapshot.mode === BOARD_MODES.NOTEBOOK ? BOARD_MODES.NOTEBOOK : BOARD_MODES.WHITEBOARD);
      setNotebook(normalizeNotebook(snapshot.notebook));
      setSelectedUploadAssetId(null);
      setSelectedUploadPage(1);
      setActiveElementAnnotationId(null);
      setSelectedElementIds([]);
      setSelectedShapeIds([]);
      setSelectedPathIds([]);
      if (snapshot.viewport) {
        setZoom(snapshot.viewport.zoom || 1);
        setPanX(snapshot.viewport.panX || 0);
        setPanY(snapshot.viewport.panY || 0);
      }
    }
  }, [snapshot]);

  // API ref for exposing methods to parent - will be populated later after handlers are defined
  const apiRef = useRef({});

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      if (localInteractionActiveRef.current) {
        pendingInteractionChangeRef.current = true;
        if (!pendingInteractionReasonRef.current) {
          pendingInteractionReasonRef.current = 'interaction-end';
        }
        return;
      }
      const reason = changeReasonRef.current || 'change';
      changeReasonRef.current = null;
      onChange(reason);
    }
  }, [paths, elements, shapes, uploads, tombstones, boardMode, notebook, onChange]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.debug('[whiteboard] canvas diagnostics', {
      graphs: elements.filter((element) => element.type === ElementType.CHART).length,
      historySize: history.length,
      pendingStrokeRaf: strokeDrawRafRef.current != null,
      pendingMinimapRaf: minimapDrawRafRef.current != null,
    });
  }, [elements, history.length]);

  const pushHistorySnapshot = useCallback(() => {
    const nextSnapshot = {
      paths: [...paths],
      shapes: [...shapes],
      elements: [...elements],
      tombstones: [...tombstones],
      uploads: [...uploads],
      boardMode,
      notebook,
    };
    const historyKey = safeHistoryStringify(nextSnapshot);
    if (lastHistorySnapshotRef.current === historyKey) {
      return;
    }
    lastHistorySnapshotRef.current = historyKey;
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndexRef.current + 1);
      if (newHistory.length >= 50) {
        newHistory.shift();
      }
      return [...newHistory, nextSnapshot];
    });
    setHistoryIndex(prev => {
      const next = Math.min(prev + 1, 49);
      historyIndexRef.current = next;
      return next;
    });
  }, [boardMode, elements, notebook, paths, shapes, tombstones, uploads]);

  // Track history for undo
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Skip if we're in the middle of undoing
    if (isUndoingRef.current) {
      isUndoingRef.current = false;
      return;
    }
    if (suppressHistoryRef.current) {
      return;
    }

    pushHistorySnapshot();
  }, [paths, shapes, elements, uploads, tombstones, pushHistorySnapshot]); // historyIndex intentionally excluded to prevent loops
  /* eslint-enable react-hooks/exhaustive-deps */

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return; // Nothing to undo

    const prevIndex = historyIndex - 1;
    const prevState = history[prevIndex];

    if (prevState) {
      isUndoingRef.current = true;
      setPaths(normalizePaths(prevState.paths));
      setShapes(prevState.shapes);
      setElements(prevState.elements);
      setTombstones(prevState.tombstones || []);
      setUploads(prevState.uploads || []);
      setBoardMode(prevState.boardMode || BOARD_MODES.WHITEBOARD);
      setNotebook(normalizeNotebook(prevState.notebook));
      historyIndexRef.current = prevIndex;
      setHistoryIndex(prevIndex);
    }
  }, [history, historyIndex]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return; // Nothing to redo

    const nextIndex = historyIndex + 1;
    const nextState = history[nextIndex];

    if (nextState) {
      isUndoingRef.current = true;
      setPaths(normalizePaths(nextState.paths));
      setShapes(nextState.shapes);
      setElements(nextState.elements);
      setTombstones(nextState.tombstones || []);
      setUploads(nextState.uploads || []);
      setBoardMode(nextState.boardMode || BOARD_MODES.WHITEBOARD);
      setNotebook(normalizeNotebook(nextState.notebook));
      historyIndexRef.current = nextIndex;
      setHistoryIndex(nextIndex);
    }
  }, [history, historyIndex]);

  // Initialize Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const updateSize = () => {
      const areaEl = canvasAreaRef.current || containerRef.current;
      if (!areaEl) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = areaEl.getBoundingClientRect();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setCanvasSize({ width: rect.width, height: rect.height });
      setResizeTrigger(prev => prev + 1);
    };

    setContext(ctx);
    updateSize();

    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Draw a shape on canvas
  const drawShape = useCallback((ctx, shape) => {
    if (!shape) return;
    ctx.beginPath();
    ctx.strokeStyle = shape.color;
    ctx.lineWidth = shape.strokeWidth;
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    const { x1, y1, x2, y2, type } = shape;

    switch (type) {
      case ShapeType.RECTANGLE:
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        if (shape.fill) {
          ctx.fillStyle = shape.fill;
          ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        }
        break;
      case ShapeType.CIRCLE:
        const centerX = (x1 + x2) / 2;
        const centerY = (y1 + y2) / 2;
        const radiusX = Math.abs(x2 - x1) / 2;
        const radiusY = Math.abs(y2 - y1) / 2;
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.stroke();
        if (shape.fill) {
          ctx.fillStyle = shape.fill;
          ctx.fill();
        }
        break;
      case ShapeType.TRIANGLE: {
        const vertices = getTriangleVertices(shape);
        ctx.moveTo(vertices[0].x, vertices[0].y);
        ctx.lineTo(vertices[1].x, vertices[1].y);
        ctx.lineTo(vertices[2].x, vertices[2].y);
        ctx.closePath();
        if (shape.fill) {
          ctx.fillStyle = shape.fill;
          ctx.fill();
        }
        ctx.stroke();
        if (shape.showAngle) {
          const angles = getTriangleAngles(vertices);
          const centroid = {
            x: (vertices[0].x + vertices[1].x + vertices[2].x) / 3,
            y: (vertices[0].y + vertices[1].y + vertices[2].y) / 3,
          };
          const fontSize = Math.max(10, Math.round(11 / zoom));
          ctx.save();
          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.lineWidth = 3;
          ctx.lineJoin = 'round';
          vertices.forEach((vertex, index) => {
            const labelX = vertex.x + (centroid.x - vertex.x) * 0.22;
            const labelY = vertex.y + (centroid.y - vertex.y) * 0.22;
            const text = `${angles[index]}Â°`;
            ctx.strokeStyle = 'white';
            ctx.strokeText(text, labelX, labelY);
            ctx.fillStyle = shape.color;
            ctx.fillText(text, labelX, labelY);
          });
          ctx.restore();
        }
        break;
      }
      case ShapeType.LINE:
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        break;
      case ShapeType.ARROW: {
        // Draw line
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        // Draw arrowhead
        const arrowAngle = Math.atan2(y2 - y1, x2 - x1);
        const headLen = 15;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - headLen * Math.cos(arrowAngle - Math.PI / 6), y2 - headLen * Math.sin(arrowAngle - Math.PI / 6));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - headLen * Math.cos(arrowAngle + Math.PI / 6), y2 - headLen * Math.sin(arrowAngle + Math.PI / 6));
        ctx.stroke();
        // Optional angle label
        if (shape.showAngle) {
          const rawDeg = Math.atan2(-(y2 - y1), x2 - x1) * 180 / Math.PI;
          const deg = Math.round(((rawDeg % 360) + 360) % 360);
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const fontSize = Math.max(10, Math.round(11 / zoom));
          ctx.save();
          ctx.font = `bold ${fontSize}px sans-serif`;
          // Draw a white halo for readability
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 3;
          ctx.lineJoin = 'round';
          ctx.strokeText(`${deg}°`, midX + 6, midY - 4);
          ctx.fillStyle = shape.color;
          ctx.fillText(`${deg}°`, midX + 6, midY - 4);
          ctx.restore();
        }
        break;
      }
      default:
        break;
    }
  }, [zoom]);

  // Redraw canvas when paths or shapes change
  useEffect(() => {
    if (!context || !canvasRef.current) return;

    // Clear canvas in CSS pixels (dpr is already in the context transform).
    context.clearRect(0, 0, canvasSize.width, canvasSize.height);

    // Apply zoom and pan transforms
    context.save();
    context.scale(zoom, zoom);
    context.translate(panX / zoom, panY / zoom);

    const drawPath = (path) => {
      const points = Array.isArray(path?.points) ? path.points : [];
      if (points.length < 2) return;

      context.beginPath();
      context.strokeStyle = path.tool === ToolType.ERASER ? 'rgba(71, 85, 105, 0.35)' : path.color;
      context.lineWidth = path.width;

      if (path.tool === ToolType.MARKER) {
        context.globalAlpha = 0.5;
        context.globalCompositeOperation = 'source-over';
      } else {
        context.globalAlpha = 1;
        context.globalCompositeOperation = 'source-over';
      }

      context.moveTo(points[0].x, points[0].y);

      // Quadratic curve smoothing
      for (let i = 1; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];
        const midPoint = {
          x: (p1.x + p2.x) / 2,
          y: (p1.y + p2.y) / 2
        };
        context.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
      }

      // Line to last point
      const last = points[points.length - 1];
      context.lineTo(last.x, last.y);
      context.stroke();
    };

    visiblePaths.forEach(drawPath);
    if (activeStrokeRef.current) drawPath(activeStrokeRef.current);

    // Draw shapes
    visibleShapes.forEach(shape => drawShape(context, shape));
    if (currentShape) drawShape(context, currentShape);

    // Highlight selected drawn items
    context.save();
    context.setLineDash([8, 6]);
    context.strokeStyle = '#1f93fe';
    context.lineWidth = Math.max(1, 2 / zoom);
    visibleShapes.forEach((shape) => {
      if (!selectedShapeIds.includes(shape.id)) return;
      const b = getShapeBounds(shape);
      context.strokeRect(b.minX, b.minY, Math.max(1, b.maxX - b.minX), Math.max(1, b.maxY - b.minY));
    });
    visiblePaths.forEach((path) => {
      if (!selectedPathIds.includes(path.id)) return;
      const b = getPathBounds(path);
      if (!b) return;
      context.strokeRect(b.minX, b.minY, Math.max(1, b.maxX - b.minX), Math.max(1, b.maxY - b.minY));
    });
    context.restore();

    // Reset defaults
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'source-over';
    context.restore();
  }, [visiblePaths, visibleShapes, currentShape, context, resizeTrigger, zoom, panX, panY, drawShape, canvasSize, selectedShapeIds, selectedPathIds]);

  const computeContentBounds = useCallback(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasContent = false;

    const includePoint = (x, y) => {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      hasContent = true;
    };

    if (isNotebookMode) {
      notebookPageRects.forEach((rect) => {
        includePoint(rect.x, rect.y);
        includePoint(rect.x + rect.width, rect.y + rect.height);
      });
    }

    visiblePaths.forEach(path => {
      (path.points || []).forEach(point => {
        includePoint(point.x, point.y);
      });
    });

    visibleShapes.forEach(shape => {
      includePoint(shape.x1, shape.y1);
      includePoint(shape.x2, shape.y2);
    });

    visibleElements.forEach(el => {
      const width = el.width || ELEMENT_DIMENSIONS[el.type]?.width || 200;
      const height = el.height || ELEMENT_DIMENSIONS[el.type]?.height || 120;
      includePoint(el.x, el.y);
      includePoint(el.x + width, el.y + height);
    });

    if (!hasContent) {
      return { minX: 0, minY: 0, maxX: 1200, maxY: 800 };
    }

    return {
      minX: minX - 120,
      minY: minY - 120,
      maxX: maxX + 120,
      maxY: maxY + 120
    };
  }, [isNotebookMode, notebookPageRects, visibleElements, visiblePaths, visibleShapes]);

  const clampPan = useCallback((nextPanX, nextPanY, nextZoom = zoom) => {
    const bounds = computeContentBounds();
    const viewWidth = Math.max(1, canvasSize.width || 1);
    const viewHeight = Math.max(1, canvasSize.height || 1);

    const minPanX = -(bounds.maxX + SOFT_PAN_MARGIN) * nextZoom;
    const maxPanX = viewWidth - (bounds.minX - SOFT_PAN_MARGIN) * nextZoom;
    const minPanY = -(bounds.maxY + SOFT_PAN_MARGIN) * nextZoom;
    const maxPanY = viewHeight - (bounds.minY - SOFT_PAN_MARGIN) * nextZoom;

    let clampedX = nextPanX;
    let clampedY = nextPanY;

    if (minPanX > maxPanX) {
      clampedX = (minPanX + maxPanX) / 2;
    } else {
      clampedX = Math.min(Math.max(nextPanX, minPanX), maxPanX);
    }

    if (minPanY > maxPanY) {
      clampedY = (minPanY + maxPanY) / 2;
    } else {
      clampedY = Math.min(Math.max(nextPanY, minPanY), maxPanY);
    }

    return { x: clampedX, y: clampedY, bounds };
  }, [canvasSize.height, canvasSize.width, computeContentBounds, zoom]);

  // Get position accounting for zoom and pan
  const getAdjustedPos = useCallback((e, canvas) => {
    const pos = getPos(e, canvas);
    return {
      x: (pos.x - panX) / zoom,
      y: (pos.y - panY) / zoom
    };
  }, [zoom, panX, panY]);

  const clientToWorld = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panX) / zoom,
      y: (clientY - rect.top - panY) / zoom,
    };
  }, [panX, panY, zoom]);

  const clearSelection = useCallback(() => {
    setSelectedElementIds([]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
  }, []);

  const applySelection = useCallback((nextSelection) => {
    setSelectedElementIds(nextSelection.elementIds);
    setSelectedShapeIds(nextSelection.shapeIds);
    setSelectedPathIds(nextSelection.pathIds);
  }, []);

  const getViewportCenter = useCallback(() => ({
    x: (-panX + canvasSize.width / 2) / zoom,
    y: (-panY + canvasSize.height / 2) / zoom,
  }), [canvasSize.height, canvasSize.width, panX, panY, zoom]);

  const copySelection = useCallback(() => {
    const hasSelection = selectedShapeIds.length || selectedPathIds.length || selectedElementIds.length;
    if (!hasSelection) return false;

    const selectedShapeSet = new Set(selectedShapeIds);
    const selectedPathSet = new Set(selectedPathIds);
    const selectedElementSet = new Set(selectedElementIds);

    internalClipboardRef.current = {
      shapes: cloneClipboardValue(shapes.filter((shape) => selectedShapeSet.has(shape.id))),
      paths: cloneClipboardValue(paths.filter((path) => selectedPathSet.has(path.id))),
      elements: cloneClipboardValue(elements.filter((element) => selectedElementSet.has(element.id))),
      pasteCount: 0,
    };
    showWhiteboardToast('Copied selection', null, 2200);
    return true;
  }, [elements, paths, selectedElementIds, selectedPathIds, selectedShapeIds, shapes, showWhiteboardToast]);

  const pasteDrawings = useCallback(() => {
    const clip = internalClipboardRef.current;
    const hasClip = clip && (clip.shapes?.length || clip.paths?.length || clip.elements?.length);
    if (!hasClip) return false;

    const count = (clip.pasteCount || 0) + 1;
    internalClipboardRef.current = { ...clip, pasteCount: count };
    const dx = PASTE_OFFSET * count;
    const dy = PASTE_OFFSET * count;

    const newShapes = (clip.shapes || []).map((shape) => {
      const nextShape = normalizeShape(withNotebookPage({
        ...cloneClipboardValue(shape),
        id: generateId(),
        x1: shape.x1 + dx,
        y1: shape.y1 + dy,
        x2: shape.x2 + dx,
        y2: shape.y2 + dy,
      }, isNotebookMode ? activeNotebookPageId : shape.notebookPageId));
      return stampUpdatedMeta(nextShape);
    });

    const newPaths = (clip.paths || []).map((path) => {
      const nextPath = normalizePath(withNotebookPage({
        ...cloneClipboardValue(path),
        id: generateId(),
        points: (path.points || []).map((point) => ({
          ...point,
          x: point.x + dx,
          y: point.y + dy,
        })),
      }, isNotebookMode ? activeNotebookPageId : path.notebookPageId));
      if (!nextPath) return null;
      return stampUpdatedMeta(nextPath);
    }).filter(Boolean);

    const newElements = (clip.elements || []).map((element) => {
      const nextElement = normalizeElementDimensions({
        ...cloneClipboardValue(element),
        id: generateId(),
        x: element.x + dx,
        y: element.y + dy,
        notebookPageId: isNotebookMode ? activeNotebookPageId : element.notebookPageId,
        annotations: cloneAnnotationsWithNewIds(element.annotations),
      });
      return stampUpdatedMeta(nextElement);
    });

    markDiscreteChange();
    setShapes((prev) => [...prev, ...newShapes]);
    setPaths((prev) => [...prev, ...newPaths]);
    setElements((prev) => [...prev, ...newElements]);
    applySelection({
      elementIds: newElements.map((element) => element.id),
      shapeIds: newShapes.map((shape) => shape.id),
      pathIds: newPaths.map((path) => path.id),
    });
    setCurrentTool(ToolType.SELECT);
    showWhiteboardToast('Pasted selection', null, 2200);
    return true;
  }, [activeNotebookPageId, applySelection, isNotebookMode, markDiscreteChange, showWhiteboardToast, stampUpdatedMeta]);

  const hitTestAt = useCallback((x, y) => {
    for (let i = visibleElements.length - 1; i >= 0; i -= 1) {
      const el = visibleElements[i];
      const bounds = getElementBounds(el);
      if (pointInBounds(x, y, bounds)) {
        return { type: 'element', id: el.id };
      }
    }

    const tolerance = Math.max(6, 10 / zoom);

    for (let i = visibleShapes.length - 1; i >= 0; i -= 1) {
      const shape = visibleShapes[i];
      const bounds = getShapeBounds(shape);
      if (!pointInBounds(x, y, {
        minX: bounds.minX - tolerance,
        minY: bounds.minY - tolerance,
        maxX: bounds.maxX + tolerance,
        maxY: bounds.maxY + tolerance
      })) {
        continue;
      }

      if (shape.type === ShapeType.RECTANGLE) {
        const nearEdge =
          Math.abs(x - shape.x1) <= tolerance ||
          Math.abs(x - shape.x2) <= tolerance ||
          Math.abs(y - shape.y1) <= tolerance ||
          Math.abs(y - shape.y2) <= tolerance;
        if (nearEdge) return { type: 'shape', id: shape.id };
      } else if (shape.type === ShapeType.CIRCLE) {
        const cx = (shape.x1 + shape.x2) / 2;
        const cy = (shape.y1 + shape.y2) / 2;
        const rx = Math.abs(shape.x2 - shape.x1) / 2 || 1;
        const ry = Math.abs(shape.y2 - shape.y1) / 2 || 1;
        const dist = Math.abs(((x - cx) ** 2) / (rx ** 2) + ((y - cy) ** 2) / (ry ** 2) - 1);
        if (dist <= Math.max(0.15, tolerance / Math.max(rx, ry, 1))) return { type: 'shape', id: shape.id };
      } else if (shape.type === ShapeType.TRIANGLE) {
        const vertices = getTriangleVertices(shape);
        const nearEdge = shapeEdges(shape).some(([a, b]) => (
          distancePointToSegment(x, y, a.x, a.y, b.x, b.y) <= tolerance
        ));
        if (pointInTriangle({ x, y }, vertices) || nearEdge) return { type: 'shape', id: shape.id };
      } else {
        const d = distancePointToSegment(x, y, shape.x1, shape.y1, shape.x2, shape.y2);
        if (d <= tolerance) return { type: 'shape', id: shape.id };
      }
    }

    for (let i = visiblePaths.length - 1; i >= 0; i -= 1) {
      const path = visiblePaths[i];
      const points = path.points || [];
      if (points.length < 2) continue;
      const bounds = getPathBounds(path);
      if (!bounds || !pointInBounds(x, y, {
        minX: bounds.minX - tolerance,
        minY: bounds.minY - tolerance,
        maxX: bounds.maxX + tolerance,
        maxY: bounds.maxY + tolerance
      })) {
        continue;
      }
      const strokeTol = Math.max(tolerance, (path.width || 2) / 2);
      for (let p = 1; p < points.length; p += 1) {
        const prev = points[p - 1];
        const curr = points[p];
        if (distancePointToSegment(x, y, prev.x, prev.y, curr.x, curr.y) <= strokeTol) {
          return { type: 'path', id: path.id };
        }
      }
    }

    return null;
  }, [visibleElements, visiblePaths, visibleShapes, zoom]);

  const selectedBounds = useMemo(() => {
    const allBounds = [];
    visibleElements.forEach((el) => {
      if (selectedElementIds.includes(el.id)) {
        allBounds.push(getElementBounds(el));
      }
    });
    visibleShapes.forEach((shape) => {
      if (selectedShapeIds.includes(shape.id)) {
        allBounds.push(getShapeBounds(shape));
      }
    });
    visiblePaths.forEach((path) => {
      if (selectedPathIds.includes(path.id)) {
        const b = getPathBounds(path);
        if (b) allBounds.push(b);
      }
    });
    if (!allBounds.length) return null;
    return {
      minX: Math.min(...allBounds.map((b) => b.minX)),
      minY: Math.min(...allBounds.map((b) => b.minY)),
      maxX: Math.max(...allBounds.map((b) => b.maxX)),
      maxY: Math.max(...allBounds.map((b) => b.maxY)),
    };
  }, [selectedElementIds, selectedPathIds, selectedShapeIds, visibleElements, visiblePaths, visibleShapes]);

  const selectedCount = selectedElementIds.length + selectedShapeIds.length + selectedPathIds.length;
  const showSelectionFrame = !!selectedBounds && (selectedCount > 1 || selectedShapeIds.length === 1 || selectedPathIds.length === 1);
  const hasPathSelection = selectedPathIds.length > 0;
  const pathSelectionResizable = useMemo(() => {
    if (!hasPathSelection || selectedElementIds.length || selectedShapeIds.length) return false;
    const selectedPathSet = new Set(selectedPathIds);
    const selectedPaths = visiblePaths.filter((path) => selectedPathSet.has(path.id));
    return selectedPaths.length > 0 && selectedPaths.every(isPathGroupResizable);
  }, [hasPathSelection, visiblePaths, selectedElementIds.length, selectedPathIds, selectedShapeIds.length]);
  const canResizeSelection =
    (selectedShapeIds.length === 1 && selectedElementIds.length === 0 && selectedPathIds.length === 0) ||
    pathSelectionResizable;
  const canRotateSelection = useMemo(() => {
    if (selectedElementIds.length) return false;
    if (!selectedShapeIds.length && !selectedPathIds.length) return false;
    const selectedShapeSet = new Set(selectedShapeIds);
    const selectedShapes = visibleShapes.filter((shape) => selectedShapeSet.has(shape.id));
    if (selectedShapes.length !== selectedShapeIds.length) return false;
    return selectedShapes.every((shape) => shape.type === ShapeType.LINE || shape.type === ShapeType.ARROW);
  }, [selectedElementIds.length, selectedPathIds.length, selectedShapeIds, visibleShapes]);

  const selectedShape = useMemo(() => {
    if (selectedShapeIds.length !== 1 || selectedElementIds.length || selectedPathIds.length) return null;
    return visibleShapes.find(sh => sh.id === selectedShapeIds[0]) || null;
  }, [selectedShapeIds, selectedElementIds, selectedPathIds, visibleShapes]);

  const toggleSelectedShapeAngles = useCallback(() => {
    if (!selectedShape) return;
    setShapes(prev => prev.map(s =>
      s.id === selectedShape.id ? stampUpdatedMeta({ ...s, showAngle: !s.showAngle }) : s
    ));
    markDiscreteChange();
  }, [selectedShape, stampUpdatedMeta, markDiscreteChange]);

  const isPointerEventPrimary = useCallback((e) => {
    if (!e) return true;
    if (typeof e.isPrimary === 'boolean' && !e.isPrimary) return false;
    const activeId = activePointerRef.current.id;
    if (activeId == null) return true;
    if (typeof e.pointerId === 'number') {
      return e.pointerId === activeId;
    }
    return true;
  }, []);

  const getStrokeWidthForEvent = useCallback((e, tool, fallbackWidth) => {
    if (!e || tool === ToolType.ERASER) return fallbackWidth;
    if (e.pointerType !== 'pen') return fallbackWidth;
    const pressure = Number(e.pressure || 0);
    if (!Number.isFinite(pressure) || pressure <= 0) return fallbackWidth;
    const multiplier = 0.55 + Math.min(pressure, 1) * 1.15;
    return Math.max(1, Math.min(64, fallbackWidth * multiplier));
  }, []);

  const drawStrokeSegment = useCallback((ctx, segment) => {
    const { from, to, path } = segment;
    if (!from || !to || !path) return;

    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.translate(panX / zoom, panY / zoom);
    ctx.beginPath();
    ctx.strokeStyle = path.tool === ToolType.ERASER ? 'rgba(71, 85, 105, 0.35)' : path.color;
    ctx.lineWidth = path.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (path.tool === ToolType.MARKER) {
      ctx.globalAlpha = 0.5;
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
  }, [panX, panY, zoom]);

  const flushStrokeDrawQueue = useCallback(() => {
    strokeDrawRafRef.current = null;
    if (!context || strokeDrawQueueRef.current.length === 0) return;
    const queue = strokeDrawQueueRef.current;
    strokeDrawQueueRef.current = [];
    queue.forEach((segment) => drawStrokeSegment(context, segment));
  }, [context, drawStrokeSegment]);

  const queueStrokeSegment = useCallback((segment) => {
    strokeDrawQueueRef.current.push(segment);
    if (strokeDrawRafRef.current != null) return;
    strokeDrawRafRef.current = window.requestAnimationFrame(flushStrokeDrawQueue);
  }, [flushStrokeDrawQueue]);

  const applyEraserStroke = useCallback((eraserStroke) => {
    const eraserPoints = eraserStroke?.points || [];
    if (eraserPoints.length < 2) return;
    const brushRadius = Math.max(4, Number(eraserStroke.width || eraserSize) / 2);
    const deletedTombstones = [];
    const nextPaths = [];
    paths.forEach((path) => {
      const isCurrentSurface = isNotebookMode
        ? belongsToNotebookPage(path, activeNotebookPageId)
        : belongsToWhiteboard(path);
      if (!isCurrentSurface) {
        nextPaths.push(path);
        return;
      }
      if (path.tool !== ToolType.PEN && path.tool !== ToolType.MARKER) {
        nextPaths.push(path);
        return;
      }

      if (!pathIntersectsEraserStroke(path, eraserPoints, brushRadius)) {
        nextPaths.push(path);
        return;
      }

      if (eraserMode === 'stroke') {
        deletedTombstones.push(buildTombstone('paths', path.id));
        return;
      }

      const segments = splitPathByEraserStroke(path, eraserPoints, brushRadius);
      if (!segments || segments.length === 0) {
        deletedTombstones.push(buildTombstone('paths', path.id));
        return;
      }

      deletedTombstones.push(buildTombstone('paths', path.id));
      segments.forEach((segmentPoints) => {
        nextPaths.push(stampUpdatedMeta(normalizePath({
          ...path,
          id: generateId(),
          points: segmentPoints,
          tool: path.tool,
        })));
      });
    });

    const nextShapes = [];
    shapes.forEach((shape) => {
      const isCurrentSurface = isNotebookMode
        ? belongsToNotebookPage(shape, activeNotebookPageId)
        : belongsToWhiteboard(shape);
      if (!isCurrentSurface) {
        nextShapes.push(shape);
        return;
      }
      if (!shapeIntersectsEraserStroke(shape, eraserPoints, brushRadius)) {
        nextShapes.push(shape);
        return;
      }
      deletedTombstones.push(buildTombstone('shapes', shape.id));
    });

    setPaths(nextPaths);
    setShapes(nextShapes);

    appendTombstones(deletedTombstones);
    setSelectedShapeIds((prev) => prev.filter((id) => !deletedTombstones.some((t) => t?.type === 'shapes' && t.id === id)));
    setSelectedPathIds((prev) => prev.filter((id) => !deletedTombstones.some((t) => t?.type === 'paths' && t.id === id)));
  }, [activeNotebookPageId, appendTombstones, buildTombstone, eraserMode, eraserSize, isNotebookMode, paths, shapes, stampUpdatedMeta]);

  // Drawing handlers
  const handleStart = useCallback((e) => {
    if (!isPointerEventPrimary(e)) return;
    e.stopPropagation?.();
    const target = e?.target;
    const isInteractiveDescendant =
      target instanceof Element &&
      !!target.closest(
        'input, textarea, select, option, button, [contenteditable="true"], .wb-graph-editor, .wb-draggable-item, .wb-selection-delete, .wb-selection-resize, .wb-selection-rotate, .wb-worksheet, .wb-whiteboard-actions'
      );
    if (isInteractiveDescendant) {
      return;
    }
    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      e.preventDefault();
    }
    if (typeof e.pointerId === 'number') {
      activePointerRef.current = {
        id: e.pointerId,
        type: e.pointerType || null,
        pressureCapable: e.pointerType === 'pen',
      };
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    }

    const pointerType = e.pointerType || 'mouse';
    const shouldTreatTouchAsPan = (
      stylusOnlyMode &&
      pointerType === 'touch' &&
      !spacePressed &&
      currentTool !== ToolType.PAN &&
      currentTool !== ToolType.SELECT
    );

    // Check for panning: PAN tool, spacebar pressed, OR middle mouse button (button === 1)
    if (currentTool === ToolType.PAN || spacePressed || e.button === 1 || shouldTreatTouchAsPan) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
      return;
    }

    if (currentTool === ToolType.TEXT && canvasRef.current) {
      const pos = getAdjustedPos(e, canvasRef.current);
      const pageRect = isNotebookMode ? getNotebookPageAtPoint(pos) : null;
      if (isNotebookMode && !pageRect) return;
      if (pageRect) {
        setNotebook((prev) => ({ ...normalizeNotebook(prev), activePageId: pageRect.pageId }));
      }
      const textWidth = Math.min(440, (pageRect?.width || 520) - 96);
      const newText = stampUpdatedMeta(withNotebookPage({
        ...createTextElement(pos.x, pos.y),
        width: textWidth,
        height: 140,
      }, isNotebookMode ? pageRect.pageId : null));
      markDiscreteChange();
      setElements((prev) => [...prev, newText]);
      setSelectedElementIds([newText.id]);
      setSelectedShapeIds([]);
      setSelectedPathIds([]);
      setFocusedTextElementId(newText.id);
      return;
    }

    if (currentTool === ToolType.SELECT) {
      if (!canvasRef.current) return;
      const pos = getAdjustedPos(e, canvasRef.current);
      const pageRect = isNotebookMode ? getNotebookPageAtPoint(pos) : null;
      if (isNotebookMode && !pageRect) return;
      if (pageRect && pageRect.pageId !== activeNotebookPageId) {
        setNotebook((prev) => ({ ...normalizeNotebook(prev), activePageId: pageRect.pageId }));
      }
      emitLocalCursor(pos, true, currentTool);
      const hit = hitTestAt(pos.x, pos.y);
      const isShift = !!e.shiftKey;

      if (hit) {
        const nextSelection = {
          elementIds: isShift ? [...selectedElementIds] : [],
          shapeIds: isShift ? [...selectedShapeIds] : [],
          pathIds: isShift ? [...selectedPathIds] : [],
        };
        const key = hit.type === 'element' ? 'elementIds' : (hit.type === 'shape' ? 'shapeIds' : 'pathIds');
        if (isShift) {
          if (nextSelection[key].includes(hit.id)) {
            nextSelection[key] = nextSelection[key].filter((id) => id !== hit.id);
          } else {
            nextSelection[key].push(hit.id);
          }
        } else {
          nextSelection[key] = [hit.id];
        }
        applySelection(nextSelection);
        if (!isShift) {
          setIsMovingSelection(true);
          beginLocalInteraction();
          selectionDragRef.current = {
            mode: 'move',
            startX: pos.x,
            startY: pos.y,
            didMutate: false,
            elementIds: nextSelection.elementIds,
            shapeIds: nextSelection.shapeIds,
            pathIds: nextSelection.pathIds,
            elements,
            shapes,
            paths
          };
        }
      } else {
        setIsSelectingBox(true);
        setSelectionBox({
          startX: pos.x,
          startY: pos.y,
          endX: pos.x,
          endY: pos.y,
          additive: isShift
        });
      }
      return;
    }

    clearSelection();

    if (canvasRef.current) {
      const pos = getAdjustedPos(e, canvasRef.current);
      const pageRect = isNotebookMode ? getNotebookPageAtPoint(pos) : null;
      emitLocalCursor(pos, true, currentTool);

      if (isNotebookMode && !pageRect) {
        return;
      }
      if (pageRect && pageRect.pageId !== activeNotebookPageId) {
        setNotebook((prev) => ({ ...normalizeNotebook(prev), activePageId: pageRect.pageId }));
      }
      const targetNotebookPageId = isNotebookMode ? pageRect.pageId : null;

      if (currentTool === ToolType.SHAPE) {
        // Start shape drawing
        beginLocalInteraction();
        const nextShape = withNotebookPage(
          createShape(currentShapeType, pos.x, pos.y, pos.x, pos.y, currentColor, 3),
          targetNotebookPageId
        );
        setShapeStart(pos);
        setCurrentShape(nextShape);
        setIsDrawing(true);
      } else {
        // Start freehand drawing
        beginLocalInteraction();
        setIsDrawing(true);
        const baseWidth = currentTool === ToolType.MARKER ? 20 : (currentTool === ToolType.ERASER ? eraserSize : 3);
        const nextPath = withNotebookPage({
          id: generateId(),
          points: [pos],
          color: currentColor,
          width: getStrokeWidthForEvent(e, currentTool, baseWidth),
          baseWidth,
          tool: currentTool,
          pointerType
        }, targetNotebookPageId);
        activeStrokeRef.current = nextPath;
      }
    }
  }, [activeNotebookPageId, applySelection, beginLocalInteraction, clearSelection, currentTool, currentColor, currentShapeType, elements, emitLocalCursor, eraserSize, getAdjustedPos, getNotebookPageAtPoint, getStrokeWidthForEvent, hitTestAt, isNotebookMode, isPointerEventPrimary, markDiscreteChange, panX, panY, paths, selectedElementIds, selectedPathIds, selectedShapeIds, shapes, spacePressed, stampUpdatedMeta, stylusOnlyMode]);

  const handleMove = useCallback((e) => {
    if (!isPointerEventPrimary(e)) return;
    e.stopPropagation?.();
    // Handle panning
    if (isPanning) {
      e.preventDefault();
      const nextPan = clampPan(e.clientX - panStart.x, e.clientY - panStart.y);
      setPanX(nextPan.x);
      setPanY(nextPan.y);
      return;
    }

    if (currentTool === ToolType.SELECT && canvasRef.current) {
      if (e.pointerType === 'touch' || e.pointerType === 'pen') {
        e.preventDefault();
      }
      const pos = getAdjustedPos(e, canvasRef.current);
      if (!isInsideActiveNotebookPage(pos)) return;
      emitLocalCursor(pos, true, currentTool);
      if (isSelectingBox && selectionBox) {
        setSelectionBox(prev => prev ? { ...prev, endX: pos.x, endY: pos.y } : null);
      } else if (isMovingSelection && selectionDragRef.current?.mode === 'move') {
        const deltaX = pos.x - selectionDragRef.current.startX;
        const deltaY = pos.y - selectionDragRef.current.startY;
        if (Math.abs(deltaX) > 0.2 || Math.abs(deltaY) > 0.2) {
          selectionDragRef.current.didMutate = true;
          markInteractionChanged();
        }

        const selectedElementSet = new Set(selectionDragRef.current.elementIds);
        const selectedShapeSet = new Set(selectionDragRef.current.shapeIds);
        const selectedPathSet = new Set(selectionDragRef.current.pathIds);

        setElements(selectionDragRef.current.elements.map((el) => (
          selectedElementSet.has(el.id) ? stampUpdatedMeta({ ...el, x: el.x + deltaX, y: el.y + deltaY }) : el
        )));
        setShapes(selectionDragRef.current.shapes.map((shape) => (
          selectedShapeSet.has(shape.id)
            ? stampUpdatedMeta({ ...shape, x1: shape.x1 + deltaX, y1: shape.y1 + deltaY, x2: shape.x2 + deltaX, y2: shape.y2 + deltaY })
            : shape
        )));
        setPaths(selectionDragRef.current.paths.map((path) => (
          selectedPathSet.has(path.id)
            ? stampUpdatedMeta({ ...path, points: (path.points || []).map((p) => ({ x: p.x + deltaX, y: p.y + deltaY })) })
            : path
        )));
      } else if (isResizingSelection && selectionDragRef.current?.mode === 'resize') {
        const bounds = selectionDragRef.current.bounds;
        const minW = 12;
        const minH = 12;
        const nextW = Math.max(minW, pos.x - bounds.minX);
        const nextH = Math.max(minH, pos.y - bounds.minY);
        const scaleX = nextW / Math.max(minW, bounds.maxX - bounds.minX || minW);
        const scaleY = nextH / Math.max(minH, bounds.maxY - bounds.minY || minH);
        if (Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001) {
          selectionDragRef.current.didMutate = true;
          markInteractionChanged();
        }

        const resizePoint = (pt) => ({
          x: bounds.minX + (pt.x - bounds.minX) * scaleX,
          y: bounds.minY + (pt.y - bounds.minY) * scaleY
        });

        if (selectionDragRef.current.targetType === 'shape') {
          setShapes(selectionDragRef.current.shapes.map((shape) => {
            if (shape.id !== selectionDragRef.current.targetId) return shape;
            const p1 = resizePoint({ x: shape.x1, y: shape.y1 });
            const p2 = resizePoint({ x: shape.x2, y: shape.y2 });
            return stampUpdatedMeta({ ...shape, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y });
          }));
        } else if (selectionDragRef.current.targetType === 'path') {
          setPaths(selectionDragRef.current.paths.map((path) => {
            if (path.id !== selectionDragRef.current.targetId) return path;
            return stampUpdatedMeta({ ...path, points: (path.points || []).map(resizePoint) });
          }));
        } else if (selectionDragRef.current.targetType === 'path-group') {
          const selectedPathSet = new Set(selectionDragRef.current.targetIds || []);
          setPaths(selectionDragRef.current.paths.map((path) => {
            if (!selectedPathSet.has(path.id)) return path;
            return stampUpdatedMeta({ ...path, points: (path.points || []).map(resizePoint) });
          }));
        }
      } else if (isRotatingSelection && selectionDragRef.current?.mode === 'rotate') {
        const { center, startAngle } = selectionDragRef.current;
        const angleDelta = Math.atan2(pos.y - center.y, pos.x - center.x) - startAngle;
        if (Math.abs(angleDelta) > 0.001) {
          selectionDragRef.current.didMutate = true;
          markInteractionChanged();
        }
        const selectedShapeSet = new Set(selectionDragRef.current.shapeIds);
        const selectedPathSet = new Set(selectionDragRef.current.pathIds);
        setShapes(selectionDragRef.current.shapes.map((shape) => {
          if (!selectedShapeSet.has(shape.id)) return shape;
          if (shape.type !== ShapeType.LINE && shape.type !== ShapeType.ARROW) return shape;
          const p1 = rotatePointAround({ x: shape.x1, y: shape.y1 }, center, angleDelta);
          const p2 = rotatePointAround({ x: shape.x2, y: shape.y2 }, center, angleDelta);
          return stampUpdatedMeta({ ...shape, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y });
        }));
        setPaths(selectionDragRef.current.paths.map((path) => (
          selectedPathSet.has(path.id)
            ? stampUpdatedMeta({ ...path, points: (path.points || []).map((point) => rotatePointAround(point, center, angleDelta)) })
            : path
        )));
      }
      return;
    }

    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();

    if (currentTool === ToolType.SHAPE && shapeStart) {
      const pos = getAdjustedPos(e, canvasRef.current);
      emitLocalCursor(pos, true, currentTool);
      // Update shape preview
      setCurrentShape(prev => prev ? {
        ...prev,
        x2: pos.x,
        y2: pos.y
      } : null);
      markInteractionChanged();
    } else if (activeStrokeRef.current) {
      const events = typeof e.getCoalescedEvents === 'function'
        ? e.getCoalescedEvents()
        : [e];
      events.forEach((pointEvent) => {
        const activeStroke = activeStrokeRef.current;
        if (!activeStroke || !canvasRef.current) return;
        const pos = getAdjustedPos(pointEvent, canvasRef.current);
        if (!isInsideActiveNotebookPage(pos)) return;
        emitLocalCursor(pos, true, activeStroke.tool);
        const lastPoint = activeStroke.points[activeStroke.points.length - 1];
        const width = getStrokeWidthForEvent(
          pointEvent,
          activeStroke.tool,
          Number.isFinite(activeStroke.baseWidth) ? activeStroke.baseWidth : activeStroke.width
        );
        activeStroke.width = width;
        activeStroke.points = [...activeStroke.points, pos];
        markInteractionChanged();
        queueStrokeSegment({
          from: lastPoint,
          to: pos,
          path: activeStroke
        });
      });
    }
  }, [isDrawing, currentTool, emitLocalCursor, shapeStart, getAdjustedPos, getStrokeWidthForEvent, isPanning, isPointerEventPrimary, panStart, clampPan, isSelectingBox, selectionBox, isMovingSelection, isResizingSelection, isRotatingSelection, isInsideActiveNotebookPage, markInteractionChanged, queueStrokeSegment, stampUpdatedMeta]);

  const handleEnd = useCallback((e) => {
    if (!isPointerEventPrimary(e)) return;
    e.stopPropagation?.();
    if (typeof e?.pointerId === 'number') {
      e.currentTarget?.releasePointerCapture?.(e.pointerId);
    }
    activePointerRef.current = { id: null, type: null, pressureCapable: false };
    if (canvasRef.current) {
      const pos = getAdjustedPos(e, canvasRef.current);
      emitLocalCursor(pos, false, currentTool);
    }

    // End panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (currentTool === ToolType.SELECT) {
      if (isSelectingBox && selectionBox) {
        const rect = {
          minX: Math.min(selectionBox.startX, selectionBox.endX),
          minY: Math.min(selectionBox.startY, selectionBox.endY),
          maxX: Math.max(selectionBox.startX, selectionBox.endX),
          maxY: Math.max(selectionBox.startY, selectionBox.endY),
        };
        const isClick = Math.abs(rect.maxX - rect.minX) < 3 && Math.abs(rect.maxY - rect.minY) < 3;
        if (isClick) {
          if (!selectionBox.additive) clearSelection();
        } else {
          const next = {
            elementIds: selectionBox.additive ? [...selectedElementIds] : [],
            shapeIds: selectionBox.additive ? [...selectedShapeIds] : [],
            pathIds: selectionBox.additive ? [...selectedPathIds] : [],
          };

          const addUnique = (arr, id) => (arr.includes(id) ? arr : [...arr, id]);

          visibleElements.forEach((el) => {
            if (boundsIntersect(rect, getElementBounds(el))) {
              next.elementIds = addUnique(next.elementIds, el.id);
            }
          });
          visibleShapes.forEach((shape) => {
            if (boundsIntersect(rect, getShapeBounds(shape))) {
              next.shapeIds = addUnique(next.shapeIds, shape.id);
            }
          });
          visiblePaths.forEach((path) => {
            const b = getPathBounds(path);
            if (b && boundsIntersect(rect, b)) {
              next.pathIds = addUnique(next.pathIds, path.id);
            }
          });
          applySelection(next);
        }
      }
      setSelectionBox(null);
      setIsSelectingBox(false);
      setIsMovingSelection(false);
      setIsResizingSelection(false);
      setIsRotatingSelection(false);
      const dragState = selectionDragRef.current;
      const didMutate = !!dragState?.didMutate;
      const wasInteractingWithSelection = Boolean(dragState);
      suppressHistoryRef.current = false;
      selectionDragRef.current = null;
      if (didMutate) {
        pushHistorySnapshot();
      }
      if (wasInteractingWithSelection) {
        endLocalInteraction(didMutate);
      }
      return;
    }

    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentTool === ToolType.SHAPE && currentShape) {
      // Finalize shape
      const width = Math.abs(currentShape.x2 - currentShape.x1);
      const height = Math.abs(currentShape.y2 - currentShape.y1);
      let didCommitShape = false;
      // Only add shape if it has some size
      if (width > 5 || height > 5) {
        setShapes(prev => [...prev, stampUpdatedMeta(normalizeShape(currentShape))]);
        didCommitShape = true;
      }
      setCurrentShape(null);
      setShapeStart(null);
      endLocalInteraction(didCommitShape);
    } else {
      let didCommitStroke = false;
      if (strokeDrawRafRef.current != null) {
        window.cancelAnimationFrame(strokeDrawRafRef.current);
        strokeDrawRafRef.current = null;
        flushStrokeDrawQueue();
      }

      if (currentTool === ToolType.ERASER && activeStrokeRef.current && Array.isArray(activeStrokeRef.current.points) && activeStrokeRef.current.points.length > 1) {
        applyEraserStroke(activeStrokeRef.current);
        didCommitStroke = true;
      } else if (activeStrokeRef.current && Array.isArray(activeStrokeRef.current.points) && activeStrokeRef.current.points.length > 1) {
        const normalizedCurrentPath = normalizePath(activeStrokeRef.current);
        if (normalizedCurrentPath && normalizedCurrentPath.points.length > 1) {
          setPaths(prev => [...prev, stampUpdatedMeta(normalizedCurrentPath)]);
          didCommitStroke = true;
        }
      }
      endLocalInteraction(didCommitStroke);
    }
    activeStrokeRef.current = null;
    strokeDrawQueueRef.current = [];
  }, [applyEraserStroke, applySelection, clearSelection, currentTool, currentShape, emitLocalCursor, endLocalInteraction, flushStrokeDrawQueue, getAdjustedPos, isDrawing, isPanning, isPointerEventPrimary, isSelectingBox, pushHistorySnapshot, selectedElementIds, selectedPathIds, selectedShapeIds, selectionBox, stampUpdatedMeta, visibleElements, visiblePaths, visibleShapes]);

  useEffect(() => () => {
    if (strokeDrawRafRef.current != null) {
      window.cancelAnimationFrame(strokeDrawRafRef.current);
    }
    if (interactionEndRafRef.current != null) {
      window.cancelAnimationFrame(interactionEndRafRef.current);
    }
    if (minimapDrawRafRef.current != null) {
      window.cancelAnimationFrame(minimapDrawRafRef.current);
    }
    if (pasteFallbackTimerRef.current != null) {
      clearTimeout(pasteFallbackTimerRef.current);
      pasteFallbackTimerRef.current = null;
    }
    if (aiMessageTimeoutRef.current != null) {
      clearTimeout(aiMessageTimeoutRef.current);
      aiMessageTimeoutRef.current = null;
    }
  }, []);

  // Element handlers
  const updateElement = useCallback((id, updates) => {
    setElements(prev => prev.map(el =>
      el.id === id ? stampUpdatedMeta({ ...el, ...updates }) : el
    ));
  }, [stampUpdatedMeta]);

  const deleteElement = useCallback((id) => {
    markDiscreteChange();
    setElements(prev => prev.filter(el => el.id !== id));
    appendTombstones([buildTombstone('elements', id)]);
    setSelectedElementIds(prev => prev.filter(selected => selected !== id));
  }, [appendTombstones, buildTombstone, markDiscreteChange]);

  const moveElement = useCallback((id, x, y) => {
    markInteractionChanged();
    setElements(prev => prev.map(el =>
      el.id === id ? stampUpdatedMeta({ ...el, x, y }) : el
    ));
  }, [markInteractionChanged, stampUpdatedMeta]);

  const resizeElement = useCallback((id, width, height) => {
    markInteractionChanged();
    setElements(prev => prev.map(el => {
      if (el.id !== id) return el;
      const defaults = ELEMENT_DIMENSIONS[el.type] || {};
      const clampedW = Math.max(defaults.minWidth || 0, width);
      const clampedH = Math.max(defaults.minHeight || 0, height);
      return stampUpdatedMeta({ ...el, width: clampedW, height: clampedH });
    }));
  }, [markInteractionChanged, stampUpdatedMeta]);

  const deleteSelection = useCallback(() => {
    const selectedElementSet = new Set(selectedElementIds);
    const selectedShapeSet = new Set(selectedShapeIds);
    const selectedPathSet = new Set(selectedPathIds);
    if (!selectedElementSet.size && !selectedShapeSet.size && !selectedPathSet.size) return;
    markDiscreteChange();
    const deleted = [
      ...Array.from(selectedElementSet).map((id) => buildTombstone('elements', id)),
      ...Array.from(selectedShapeSet).map((id) => buildTombstone('shapes', id)),
      ...Array.from(selectedPathSet).map((id) => buildTombstone('paths', id)),
    ];
    setElements(prev => prev.filter((el) => !selectedElementSet.has(el.id)));
    setShapes(prev => prev.filter((shape) => !selectedShapeSet.has(shape.id)));
    setPaths(prev => prev.filter((path) => !selectedPathSet.has(path.id)));
    appendTombstones(deleted);
    clearSelection();
  }, [appendTombstones, buildTombstone, clearSelection, markDiscreteChange, selectedElementIds, selectedPathIds, selectedShapeIds]);

  const getViewportCenterWorld = useCallback(() => {
    const width = canvasSize.width || 1200;
    const height = canvasSize.height || 800;
    const worldX = (width / 2 - panX) / zoom;
    const worldY = (height / 2 - panY) / zoom;
    return { x: worldX, y: worldY };
  }, [canvasSize.height, canvasSize.width, panX, panY, zoom]);

  // Find an empty position near the current viewport center.
  const findEmptyPosition = useCallback((width = 200, height = 100) => {
    const PADDING = 24;
    const STEP = 36;
    const occupied = visibleElements.map(el => ({
      x: el.x,
      y: el.y,
      width: el.width || 200,
      height: el.height || 100
    }));

    const burstNow = Date.now();
    const burst = insertionBurstRef.current;
    const nextBurstCount = burstNow - burst.timestamp < 360 ? burst.count + 1 : 0;
    insertionBurstRef.current = { timestamp: burstNow, count: nextBurstCount };
    const burstOffset = nextBurstCount * 18;

    const center = isNotebookMode
      ? {
        x: activeNotebookPageRect.x + activeNotebookPageRect.width / 2,
        y: activeNotebookPageRect.y + Math.min(activeNotebookPageRect.height - height / 2 - 48, 170 + burstOffset),
      }
      : getViewportCenterWorld();
    const startX = Math.round(center.x - width / 2 + (isNotebookMode ? 0 : burstOffset));
    const startY = Math.round(center.y - height / 2 + (isNotebookMode ? 0 : burstOffset));

    const isOverlapping = (x, y) => occupied.some((other) =>
      x < other.x + other.width + PADDING &&
      x + width + PADDING > other.x &&
      y < other.y + other.height + PADDING &&
      y + height + PADDING > other.y
    );

    if (!isOverlapping(startX, startY)) {
      return { x: startX, y: startY };
    }

    // Spiral search around viewport center.
    const maxRadius = 24;
    for (let radius = 1; radius <= maxRadius; radius += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) {
        for (let dy = -radius; dy <= radius; dy += 1) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
          const x = startX + dx * STEP;
          const y = startY + dy * STEP;
          if (!isOverlapping(x, y)) {
            return { x, y };
          }
        }
      }
    }

    return { x: startX, y: startY };
  }, [activeNotebookPageRect, getViewportCenterWorld, isNotebookMode, visibleElements]);

  // Add element handlers - now accept optional initial content from AI
  const handleAddNote = useCallback((initialContent = '', initialColor = null) => {
    const safeContent = sanitizeOptionalText(initialContent);
    const safeColor = sanitizeOptionalText(initialColor).toLowerCase();
    const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.NOTE].width, ELEMENT_DIMENSIONS[ElementType.NOTE].height);
    const newNote = stampUpdatedMeta(withNotebookPage(
      createNoteElement(pos.x, pos.y),
      isNotebookMode ? activeNotebookPageId : null
    ));
    if (safeContent) {
      newNote.content = safeContent;
    }
    if (safeColor) {
      // Map color names to NOTE_COLORS
      const colorMap = {
        'yellow': NOTE_COLORS[0],
        'pink': NOTE_COLORS[1],
        'blue': NOTE_COLORS[2],
        'green': NOTE_COLORS[3],
        'purple': NOTE_COLORS[4]
      };
      newNote.color = colorMap[safeColor] || NOTE_COLORS[0];
    }
    markDiscreteChange();
    setElements(prev => [...prev, newNote]);
    setSelectedElementIds([newNote.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [activeNotebookPageId, findEmptyPosition, isNotebookMode, markDiscreteChange, stampUpdatedMeta]);

  const handleAddText = useCallback((initialContent = '') => {
    const safeContent = sanitizeOptionalText(initialContent);
    const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.TEXT].width, ELEMENT_DIMENSIONS[ElementType.TEXT].height);
    const newText = stampUpdatedMeta(withNotebookPage(
      createTextElement(pos.x, pos.y),
      isNotebookMode ? activeNotebookPageId : null
    ));
    if (safeContent) {
      newText.content = safeContent;
    }
    markDiscreteChange();
    setElements(prev => [...prev, newText]);
    setSelectedElementIds([newText.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [activeNotebookPageId, findEmptyPosition, isNotebookMode, markDiscreteChange, stampUpdatedMeta]);

  const handleAddMath = useCallback((initialExpression = '') => {
    const safeExpression = sanitizeOptionalText(initialExpression);
    const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.MATH].width, ELEMENT_DIMENSIONS[ElementType.MATH].height);
    const newMath = stampUpdatedMeta(withNotebookPage(
      createMathElement(pos.x, pos.y),
      isNotebookMode ? activeNotebookPageId : null
    ));
    if (safeExpression) {
      newMath.expression = safeExpression;
      newMath.rawInput = safeExpression;
      newMath.isEditing = false; // Already has content, show rendered view
    }
    markDiscreteChange();
    setElements(prev => [...prev, newMath]);
    setSelectedElementIds([newMath.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [activeNotebookPageId, findEmptyPosition, isNotebookMode, markDiscreteChange, stampUpdatedMeta]);

  const handleAddChart = useCallback((initialExpression = '') => {
    const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.CHART].width, ELEMENT_DIMENSIONS[ElementType.CHART].height);
    const newChart = stampUpdatedMeta(withNotebookPage(
      createChartElement(pos.x, pos.y),
      isNotebookMode ? activeNotebookPageId : null
    ));
    newChart.expressions = normalizeGraphExpressions(initialExpression, DEFAULT_GRAPH_EXPRESSION);
    markDiscreteChange();
    setElements(prev => [...prev, newChart]);
    setSelectedElementIds([newChart.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [activeNotebookPageId, findEmptyPosition, isNotebookMode, markDiscreteChange, stampUpdatedMeta]);

  const handleAddFbd = useCallback(() => {
    const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.FBD].width, ELEMENT_DIMENSIONS[ElementType.FBD].height);
    const newFbd = stampUpdatedMeta(withNotebookPage(
      createFbdElement(pos.x, pos.y),
      isNotebookMode ? activeNotebookPageId : null
    ));
    markDiscreteChange();
    setElements(prev => [...prev, newFbd]);
    setSelectedElementIds([newFbd.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [activeNotebookPageId, findEmptyPosition, isNotebookMode, markDiscreteChange, stampUpdatedMeta]);

  const fileToDataUrl = useCallback((file) => (
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    })
  ), []);

  const handlePaste = useCallback(async (event) => {
    if (isEditableTarget(event.target)) return;

    pendingInternalPasteFallbackRef.current = false;
    if (pasteFallbackTimerRef.current != null) {
      clearTimeout(pasteFallbackTimerRef.current);
      pasteFallbackTimerRef.current = null;
    }

    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type?.startsWith('image/'));

    if (!imageItem) {
      if (pasteDrawings()) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    const file = imageItem.getAsFile();
    if (!file) return;

    try {
      const src = await fileToDataUrl(file);
      const dimensions = fitImageToWorksheetBox(await readImageDimensions(src));
      const center = getViewportCenter();
      const worksheet = stampUpdatedMeta(createWorksheetElement({
        x: center.x - dimensions.width / 2,
        y: center.y - dimensions.height / 2,
        width: dimensions.width,
        height: dimensions.height,
        assetId: generateId(),
        assetType: 'image',
        src,
      }));

      if (isNotebookMode) {
        const page = createDefaultNotebookPage(`Image ${notebook.pages.length + 1}`, {
          backgroundType: NOTEBOOK_BACKGROUNDS.IMPORTED,
          sourceAssetId: worksheet.assetId,
          sourcePageNumber: 1,
          backgroundSrc: src,
        });
        markDiscreteChange();
        setNotebook((prev) => ({
          pages: [...normalizeNotebook(prev).pages, page],
          activePageId: page.id,
        }));
        showWhiteboardToast('Pasted image as notebook page', null, 2200);
        return;
      }

      markDiscreteChange();
      setElements((prev) => [...prev, worksheet]);
      setSelectedElementIds([worksheet.id]);
      setSelectedShapeIds([]);
      setSelectedPathIds([]);
      setCurrentTool(ToolType.SELECT);
      showWhiteboardToast('Pasted image', null, 2200);
    } catch (error) {
      console.error('Clipboard image paste failed:', error);
    }
  }, [fileToDataUrl, getViewportCenter, isNotebookMode, markDiscreteChange, notebook.pages.length, pasteDrawings, showWhiteboardToast, stampUpdatedMeta]);

  const renderPdfPageToDataUrl = useCallback(async (pdfDoc, pageNumber) => {
    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Unable to render PDF page');
    }
    await page.render({ canvasContext: ctx, viewport }).promise;
    return {
      pageNumber,
      width: canvas.width,
      height: canvas.height,
      src: canvas.toDataURL('image/jpeg', 0.92),
    };
  }, []);

  const handleUploadFiles = useCallback(async (files) => {
    if (!files.length) return;
    setIsProcessingUpload(true);
    setUploadError(null);
    setUploadTrayExpanded(true);
    if (process.env.NODE_ENV !== 'production') {
      // Dev-only trace to confirm worker source at upload time.
      console.info('PDF.js workerSrc:', pdfjsLib?.GlobalWorkerOptions?.workerSrc || '<unset>');
    }

    try {
      const nextAssets = [];
      for (const file of files) {
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_UPLOAD_SIZE_MB) {
          throw new Error(`${file.name} exceeds ${MAX_UPLOAD_SIZE_MB}MB`);
        }

        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          const arrayBuffer = await file.arrayBuffer();
          const pdfDoc = await loadPdfDocument(arrayBuffer);
          const totalPages = Math.min(pdfDoc.numPages, MAX_PDF_PAGES);
          const pages = [];
          for (let pageNum = 1; pageNum <= totalPages; pageNum += 1) {
            // eslint-disable-next-line no-await-in-loop
            const rendered = await renderPdfPageToDataUrl(pdfDoc, pageNum);
            pages.push(rendered);
          }
          nextAssets.push({
            id: generateId(),
            type: 'pdf',
            name: file.name,
            pageCount: pdfDoc.numPages,
            pages,
            createdAt: Date.now(),
          });
        } else if (file.type.startsWith('image/')) {
          // eslint-disable-next-line no-await-in-loop
          const dataUrl = await fileToDataUrl(file);
          nextAssets.push({
            id: generateId(),
            type: 'image',
            name: file.name,
            pageCount: 1,
            pages: [{ pageNumber: 1, src: dataUrl }],
            createdAt: Date.now(),
          });
        }
      }

      if (!nextAssets.length) {
        throw new Error('No supported files selected');
      }

      setUploads((prev) => {
        const combined = [...nextAssets, ...prev];
        const first = combined[0];
        if (first) {
          setSelectedUploadAssetId(first.id);
          setSelectedUploadPage(1);
        }
        return combined;
      });
    } catch (error) {
      console.error('Upload processing failed:', error);
      const isPdfProcessingError = isPdfWorkerLoadError(error) || isPdfCompatibilityError(error);
      const friendlyMessage = isPdfProcessingError
        ? 'Could not process this PDF. Try re-uploading or using a different PDF.'
        : (error?.message || 'Could not process uploaded file');
      setUploadError(friendlyMessage);
    } finally {
      setIsProcessingUpload(false);
    }
  }, [fileToDataUrl, renderPdfPageToDataUrl]);

  const handlePasteSelectedUpload = useCallback(() => {
    const asset = uploads.find((item) => item.id === selectedUploadAssetId);
    if (!asset) return;

    const page = asset.type === 'pdf'
      ? (asset.pages || []).find((p) => p.pageNumber === selectedUploadPage)
      : (asset.pages || [])[0];
    if (!page?.src) return;

    const defaultWidth = ELEMENT_DIMENSIONS[ElementType.WORKSHEET].width;
    const ratio = page.width && page.height ? (page.height / page.width) : 1.33;
    const width = defaultWidth;
    const height = Math.max(ELEMENT_DIMENSIONS[ElementType.WORKSHEET].minHeight, Math.round(defaultWidth * ratio));
    const pos = findEmptyPosition(width, height);

    const worksheet = stampUpdatedMeta(createWorksheetElement({
      x: pos.x,
      y: pos.y,
      width,
      height,
      assetId: asset.id,
      assetType: asset.type === 'pdf' ? 'pdf-page' : 'image',
      src: page.src,
      pdfMeta: asset.type === 'pdf'
        ? {
            pageNumber: page.pageNumber,
            totalPages: asset.pageCount,
            filename: asset.name,
          }
        : null,
    }));
    if (isNotebookMode) {
      const pageTitle = asset.type === 'pdf'
        ? `${asset.name} p.${page.pageNumber}`
        : asset.name;
      const notebookPage = createDefaultNotebookPage(pageTitle, {
        backgroundType: NOTEBOOK_BACKGROUNDS.IMPORTED,
        sourceAssetId: asset.id,
        sourcePageNumber: page.pageNumber,
        backgroundSrc: page.src,
      });
      markDiscreteChange();
      setNotebook((prev) => ({
        pages: [...normalizeNotebook(prev).pages, notebookPage],
        activePageId: notebookPage.id,
      }));
      clearSelection();
      setCurrentTool(ToolType.SELECT);
      return;
    }
    markDiscreteChange();
    setElements((prev) => [...prev, worksheet]);
    setSelectedElementIds([worksheet.id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
    setCurrentTool(ToolType.SELECT);
  }, [clearSelection, findEmptyPosition, isNotebookMode, markDiscreteChange, selectedUploadAssetId, selectedUploadPage, stampUpdatedMeta, uploads]);

  const handleInsertUploadAsNotebookPages = useCallback(() => {
    const asset = uploads.find((item) => item.id === selectedUploadAssetId);
    if (!asset) return;
    const pages = asset.type === 'pdf'
      ? (asset.pages || [])
      : (asset.pages || []).slice(0, 1);
    if (!pages.length) return;
    const nextPages = pages.map((page) => createDefaultNotebookPage(
      asset.type === 'pdf' ? `${asset.name} p.${page.pageNumber}` : asset.name,
      {
        backgroundType: NOTEBOOK_BACKGROUNDS.IMPORTED,
        sourceAssetId: asset.id,
        sourcePageNumber: page.pageNumber,
        backgroundSrc: page.src,
      }
    ));
    markDiscreteChange();
    setBoardMode(BOARD_MODES.NOTEBOOK);
    setNotebook((prev) => {
      const current = normalizeNotebook(prev);
      return {
        pages: [...current.pages, ...nextPages],
        activePageId: nextPages[0].id,
      };
    });
    clearSelection();
    setCurrentTool(ToolType.SELECT);
  }, [clearSelection, markDiscreteChange, selectedUploadAssetId, uploads]);

  // Handler for AI text responses (shown as toast with optional neuro-technique badge)
  const handleAiMessage = useCallback((message, technique = null) => {
    showWhiteboardToast(message, technique);
  }, [showWhiteboardToast]);

  const handleModeChange = useCallback((nextMode) => {
    const normalizedMode = nextMode === BOARD_MODES.NOTEBOOK ? BOARD_MODES.NOTEBOOK : BOARD_MODES.WHITEBOARD;
    markDiscreteChange();
    setBoardMode(normalizedMode);
    setNotebook((prev) => normalizeNotebook(prev));
    clearSelection();
    setActiveElementAnnotationId(null);
    if (normalizedMode === BOARD_MODES.NOTEBOOK) {
      setZoom(0.85);
      setPanX((canvasSize.width - NOTEBOOK_PAGE_WIDTH * 0.85) / 2);
      setPanY(48);
    }
  }, [canvasSize.width, clearSelection, markDiscreteChange]);

  const handleNotebookPageChange = useCallback((pageId) => {
    setNotebook((prev) => {
      const current = normalizeNotebook(prev);
      if (!current.pages.some((page) => page.id === pageId)) return current;
      return { ...current, activePageId: pageId };
    });
    const rect = notebookPageRects.find((pageRect) => pageRect.pageId === pageId);
    if (rect) {
      const nextPanX = (canvasSize.width - rect.width * zoom) / 2 - rect.x * zoom;
      const nextPanY = 72 - rect.y * zoom;
      const clamped = clampPan(nextPanX, nextPanY, zoom);
      setPanX(clamped.x);
      setPanY(clamped.y);
    }
    clearSelection();
    setActiveElementAnnotationId(null);
  }, [canvasSize.width, clampPan, clearSelection, notebookPageRects, zoom]);

  const handleAddNotebookPage = useCallback(() => {
    const current = normalizeNotebook(notebook);
    const nextPage = createDefaultNotebookPage(`Page ${current.pages.length + 1}`);
    markDiscreteChange();
    setBoardMode(BOARD_MODES.NOTEBOOK);
    setNotebook({
      pages: [...current.pages, nextPage],
      activePageId: nextPage.id,
    });
    clearSelection();
  }, [clearSelection, markDiscreteChange, notebook]);

  const handleDuplicateNotebookPage = useCallback(() => {
    const current = normalizeNotebook(notebook);
    const sourcePage = current.pages.find((page) => page.id === current.activePageId) || current.pages[0];
    if (!sourcePage) return;
    const nextPage = {
      ...sourcePage,
      id: generateId(),
      title: `${sourcePage.title || 'Page'} Copy`,
    };
    const duplicateEntity = (entity) => stampUpdatedMeta({
      ...cloneClipboardValue(entity),
      id: generateId(),
      notebookPageId: nextPage.id,
      annotations: cloneAnnotationsWithNewIds(entity.annotations),
    });
    markDiscreteChange();
    setNotebook({
      pages: [...current.pages, nextPage],
      activePageId: nextPage.id,
    });
    setPaths((prev) => [...prev, ...paths.filter((path) => belongsToNotebookPage(path, sourcePage.id)).map(duplicateEntity)]);
    setShapes((prev) => [...prev, ...shapes.filter((shape) => belongsToNotebookPage(shape, sourcePage.id)).map(duplicateEntity)]);
    setElements((prev) => [...prev, ...elements.filter((element) => belongsToNotebookPage(element, sourcePage.id)).map(duplicateEntity)]);
    clearSelection();
  }, [clearSelection, elements, markDiscreteChange, notebook, paths, shapes, stampUpdatedMeta]);

  const handleDeleteNotebookPage = useCallback(() => {
    const current = normalizeNotebook(notebook);
    if (current.pages.length <= 1) return;
    const pageId = current.activePageId;
    const nextPages = current.pages.filter((page) => page.id !== pageId);
    const nextActivePageId = nextPages[0]?.id || null;
    markDiscreteChange();
    setNotebook({ pages: nextPages, activePageId: nextActivePageId });
    setPaths((prev) => prev.filter((path) => !belongsToNotebookPage(path, pageId)));
    setShapes((prev) => prev.filter((shape) => !belongsToNotebookPage(shape, pageId)));
    setElements((prev) => prev.filter((element) => !belongsToNotebookPage(element, pageId)));
    clearSelection();
  }, [clearSelection, markDiscreteChange, notebook]);

  const handleNotebookBackgroundChange = useCallback((backgroundType) => {
    const safeBackground = Object.values(NOTEBOOK_BACKGROUNDS).includes(backgroundType)
      ? backgroundType
      : NOTEBOOK_BACKGROUNDS.LINED;
    if (safeBackground === NOTEBOOK_BACKGROUNDS.IMPORTED && !activeNotebookPage?.backgroundSrc) return;
    markDiscreteChange();
    setNotebook((prev) => {
      const current = normalizeNotebook(prev);
      return {
        ...current,
        pages: current.pages.map((page) => (
          page.id === current.activePageId ? { ...page, backgroundType: safeBackground } : page
        )),
      };
    });
  }, [activeNotebookPage?.backgroundSrc, markDiscreteChange]);

  const handleNotebookDocumentChange = useCallback((pageId, documentHtml) => {
    const safeHtml = sanitizeDocumentHtml(documentHtml);
    setNotebook((prev) => {
      const current = normalizeNotebook(prev);
      return {
        ...current,
        pages: current.pages.map((page) => (
          page.id === pageId ? { ...page, documentHtml: safeHtml, documentContent: safeHtml.replace(/<[^>]*>/g, ' ') } : page
        )),
      };
    });
  }, []);

  useEffect(() => {
    if (!isNotebookMode || !notebookPageRects.length || !canvasSize.height) return;
    const viewportCenterY = (-panY + canvasSize.height / 2) / zoom;
    const nearestPage = notebookPageRects.reduce((best, rect) => {
      const center = rect.y + rect.height / 2;
      const distance = Math.abs(center - viewportCenterY);
      if (!best || distance < best.distance) {
        return { rect, distance };
      }
      return best;
    }, null)?.rect;
    if (!nearestPage || nearestPage.pageId === activeNotebookPageId) return;
    setNotebook((prev) => ({ ...normalizeNotebook(prev), activePageId: nearestPage.pageId }));
  }, [activeNotebookPageId, canvasSize.height, isNotebookMode, notebookPageRects, panY, zoom]);

  const handleClear = useCallback(() => {
    if (window.confirm('Clear all content from the whiteboard?')) {
      markDiscreteChange();
      const clearedTombstones = [
        ...elements.map((el) => buildTombstone('elements', el.id)),
        ...shapes.map((shape) => buildTombstone('shapes', shape.id)),
        ...paths.map((path) => buildTombstone('paths', path.id)),
      ];
      setPaths([]);
      setElements([]);
      setShapes([]);
      setUploads([]);
      setSelectedUploadAssetId(null);
      appendTombstones(clearedTombstones);
      clearSelection();
    }
  }, [appendTombstones, buildTombstone, clearSelection, elements, markDiscreteChange, paths, shapes]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, []);

  // Mouse wheel for zoom/pan
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      // Zoom
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(prev => Math.max(0.1, Math.min(5, prev * delta)));
    } else {
      // Pan
      const nextPan = isNotebookMode
        ? clampPan(panX, panY - e.deltaY)
        : clampPan(panX - e.deltaX, panY - e.deltaY);
      setPanX(nextPan.x);
      setPanY(nextPan.y);
    }
  }, [clampPan, isNotebookMode, panX, panY]);

  useEffect(() => {
    const nextPan = clampPan(panX, panY, zoom);
    if (Math.abs(nextPan.x - panX) > 0.5) {
      setPanX(nextPan.x);
    }
    if (Math.abs(nextPan.y - panY) > 0.5) {
      setPanY(nextPan.y);
    }
  }, [clampPan, panX, panY, zoom]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (isEditableTarget(e.target)) return;

      const shortcutModifier = e.ctrlKey || e.metaKey;

      // Handle Ctrl+Z (Undo) and Ctrl+Y / Ctrl+Shift+Z (Redo)
      if (shortcutModifier && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }
      if (shortcutModifier && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
        return;
      }
      if (shortcutModifier && e.key.toLowerCase() === 'c') {
        if (copySelection()) {
          e.preventDefault();
        }
        return;
      }
      if (shortcutModifier && e.key.toLowerCase() === 'v') {
        pendingInternalPasteFallbackRef.current = true;
        if (pasteFallbackTimerRef.current != null) {
          clearTimeout(pasteFallbackTimerRef.current);
        }
        pasteFallbackTimerRef.current = setTimeout(() => {
          pasteFallbackTimerRef.current = null;
          if (!pendingInternalPasteFallbackRef.current) return;
          pendingInternalPasteFallbackRef.current = false;
          pasteDrawings();
        }, 70);
        return;
      }
      if (shortcutModifier && /^[a-z]$/i.test(e.key)) {
        return;
      }

      // Handle spacebar for pan mode
      if (e.key === ' ' && !e.repeat) {
        e.preventDefault();
        setSpacePressed(true);
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'v':
          setCurrentTool(ToolType.SELECT);
          break;
        case 'p':
        case 'd':
          setCurrentTool(ToolType.PEN);
          break;
        case 'h':
          setCurrentTool(ToolType.MARKER);
          break;
        case 'e':
          setCurrentTool(ToolType.ERASER);
          break;
        case 's':
          setCurrentTool(ToolType.SHAPE);
          break;
        case 'n':
          handleAddNote();
          break;
        case 't':
          setCurrentTool(ToolType.TEXT);
          break;
        case 'm':
          handleAddMath();
          break;
        case 'g':
          handleAddChart();
          break;
        case 'delete':
        case 'backspace':
          if (selectedElementIds.length || selectedShapeIds.length || selectedPathIds.length) {
            deleteSelection();
          }
          break;
        case '=':
        case '+':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          handleZoomReset();
          break;
        default:
          break;
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === ' ') {
        setSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('paste', handlePaste);
    };
  }, [selectedElementIds, selectedShapeIds, selectedPathIds, copySelection, pasteDrawings, handleAddNote, handleAddMath, handleAddChart, deleteSelection, handleZoomIn, handleZoomOut, handleZoomReset, handleUndo, handleRedo, handlePaste]);

  // Export functions
  const exportToSvg = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    // Create SVG from canvas content
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const rect = containerRef.current.getBoundingClientRect();
    svg.setAttribute('width', rect.width);
    svg.setAttribute('height', rect.height);
    svg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);

    // Add background
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#f8f9fa');
    svg.appendChild(bg);

    // Convert paths to SVG paths
    normalizePaths(paths).filter((path) => path.tool !== ToolType.ERASER).forEach(path => {
      const points = Array.isArray(path?.points) ? path.points : [];
      if (points.length < 2) return;

      const svgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      let d = `M ${points[0].x} ${points[0].y}`;

      for (let i = 1; i < points.length; i++) {
        d += ` L ${points[i].x} ${points[i].y}`;
      }

      svgPath.setAttribute('d', d);
      svgPath.setAttribute('stroke', path.color);
      svgPath.setAttribute('stroke-width', path.width);
      svgPath.setAttribute('fill', 'none');
      svgPath.setAttribute('stroke-linecap', 'round');
      svgPath.setAttribute('stroke-linejoin', 'round');

      if (path.tool === ToolType.MARKER) {
        svgPath.setAttribute('opacity', '0.5');
      }

      svg.appendChild(svgPath);
    });

    // Convert shapes to SVG elements
    shapes.forEach(shape => {
      const { x1, y1, x2, y2, type, color, strokeWidth } = shape;
      let svgEl;

      switch (type) {
        case ShapeType.RECTANGLE:
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          svgEl.setAttribute('x', Math.min(x1, x2));
          svgEl.setAttribute('y', Math.min(y1, y2));
          svgEl.setAttribute('width', Math.abs(x2 - x1));
          svgEl.setAttribute('height', Math.abs(y2 - y1));
          break;
        case ShapeType.CIRCLE:
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
          svgEl.setAttribute('cx', (x1 + x2) / 2);
          svgEl.setAttribute('cy', (y1 + y2) / 2);
          svgEl.setAttribute('rx', Math.abs(x2 - x1) / 2);
          svgEl.setAttribute('ry', Math.abs(y2 - y1) / 2);
          break;
        case ShapeType.TRIANGLE: {
          const vertices = getTriangleVertices(shape);
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
          polygon.setAttribute('points', vertices.map((point) => `${point.x},${point.y}`).join(' '));
          polygon.setAttribute('stroke', color);
          polygon.setAttribute('stroke-width', strokeWidth);
          polygon.setAttribute('fill', shape.fill || 'none');
          svgEl.appendChild(polygon);
          if (shape.showAngle) {
            const angles = getTriangleAngles(vertices);
            const centroid = {
              x: (vertices[0].x + vertices[1].x + vertices[2].x) / 3,
              y: (vertices[0].y + vertices[1].y + vertices[2].y) / 3,
            };
            vertices.forEach((vertex, index) => {
              const labelX = vertex.x + (centroid.x - vertex.x) * 0.22;
              const labelY = vertex.y + (centroid.y - vertex.y) * 0.22;
              const text = `${angles[index]}Â°`;
              const halo = document.createElementNS('http://www.w3.org/2000/svg', 'text');
              halo.setAttribute('x', labelX);
              halo.setAttribute('y', labelY);
              halo.setAttribute('fill', 'none');
              halo.setAttribute('stroke', 'white');
              halo.setAttribute('stroke-width', '3');
              halo.setAttribute('stroke-linejoin', 'round');
              halo.setAttribute('font-size', '11');
              halo.setAttribute('font-weight', 'bold');
              halo.setAttribute('font-family', 'sans-serif');
              halo.textContent = text;
              svgEl.appendChild(halo);
              const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
              label.setAttribute('x', labelX);
              label.setAttribute('y', labelY);
              label.setAttribute('fill', color);
              label.setAttribute('font-size', '11');
              label.setAttribute('font-weight', 'bold');
              label.setAttribute('font-family', 'sans-serif');
              label.textContent = text;
              svgEl.appendChild(label);
            });
          }
          break;
        }
        case ShapeType.LINE:
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          svgEl.setAttribute('x1', x1);
          svgEl.setAttribute('y1', y1);
          svgEl.setAttribute('x2', x2);
          svgEl.setAttribute('y2', y2);
          break;
        case ShapeType.ARROW: {
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          const svgLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          svgLine.setAttribute('x1', x1);
          svgLine.setAttribute('y1', y1);
          svgLine.setAttribute('x2', x2);
          svgLine.setAttribute('y2', y2);
          svgLine.setAttribute('stroke', color);
          svgLine.setAttribute('stroke-width', strokeWidth);
          svgEl.appendChild(svgLine);
          // Arrowhead
          const svgArrowAngle = Math.atan2(y2 - y1, x2 - x1);
          const svgHeadLen = 15;
          const svgArrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          const svgArrowD = `M ${x2} ${y2} L ${x2 - svgHeadLen * Math.cos(svgArrowAngle - Math.PI / 6)} ${y2 - svgHeadLen * Math.sin(svgArrowAngle - Math.PI / 6)} M ${x2} ${y2} L ${x2 - svgHeadLen * Math.cos(svgArrowAngle + Math.PI / 6)} ${y2 - svgHeadLen * Math.sin(svgArrowAngle + Math.PI / 6)}`;
          svgArrow.setAttribute('d', svgArrowD);
          svgArrow.setAttribute('stroke', color);
          svgArrow.setAttribute('stroke-width', strokeWidth);
          svgArrow.setAttribute('fill', 'none');
          svgEl.appendChild(svgArrow);
          // Optional angle label
          if (shape.showAngle) {
            const svgRawDeg = Math.atan2(-(y2 - y1), x2 - x1) * 180 / Math.PI;
            const svgDeg = Math.round(((svgRawDeg % 360) + 360) % 360);
            const svgLabelX = (x1 + x2) / 2 + 6;
            const svgLabelY = (y1 + y2) / 2 - 4;
            // White halo
            const svgHalo = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            svgHalo.setAttribute('x', svgLabelX);
            svgHalo.setAttribute('y', svgLabelY);
            svgHalo.setAttribute('fill', 'none');
            svgHalo.setAttribute('stroke', 'white');
            svgHalo.setAttribute('stroke-width', '3');
            svgHalo.setAttribute('stroke-linejoin', 'round');
            svgHalo.setAttribute('font-size', '11');
            svgHalo.setAttribute('font-weight', 'bold');
            svgHalo.setAttribute('font-family', 'sans-serif');
            svgHalo.textContent = `${svgDeg}°`;
            svgEl.appendChild(svgHalo);
            const svgLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            svgLabel.setAttribute('x', svgLabelX);
            svgLabel.setAttribute('y', svgLabelY);
            svgLabel.setAttribute('fill', color);
            svgLabel.setAttribute('font-size', '11');
            svgLabel.setAttribute('font-weight', 'bold');
            svgLabel.setAttribute('font-family', 'sans-serif');
            svgLabel.textContent = `${svgDeg}°`;
            svgEl.appendChild(svgLabel);
          }
          break;
        }
        default:
          return;
      }

      if (svgEl && ![ShapeType.ARROW, ShapeType.TRIANGLE].includes(type)) {
        svgEl.setAttribute('stroke', color);
        svgEl.setAttribute('stroke-width', strokeWidth);
        svgEl.setAttribute('fill', shape.fill || 'none');
      }

      if (svgEl) svg.appendChild(svgEl);
    });

    return svg;
  }, [paths, shapes]);

  const exportToPng = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
  }, []);

  const exportToPdf = useCallback(async () => {
    if (boardMode === BOARD_MODES.NOTEBOOK) {
      const currentNotebook = normalizeNotebook(notebook);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT]
      });

      const addImageToPdf = async (src, x, y, width, height) => {
        if (!src) return;
        try {
          await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = resolve;
            image.onerror = reject;
            image.src = src;
          });
          pdf.addImage(src, 'PNG', x, y, width, height, undefined, 'FAST');
        } catch {
          try {
            pdf.addImage(src, 'JPEG', x, y, width, height, undefined, 'FAST');
          } catch (error) {
            console.warn('Skipping notebook image during PDF export:', error);
          }
        }
      };

      for (let index = 0; index < currentNotebook.pages.length; index += 1) {
        const page = currentNotebook.pages[index];
        const pageRect = getNotebookPageRect(index);
        const toPageX = (x) => x - pageRect.x;
        const toPageY = (y) => y - pageRect.y;
        if (index > 0) {
          pdf.addPage([NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT], 'portrait');
        }

        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT, 'F');

        if (page.backgroundType === NOTEBOOK_BACKGROUNDS.IMPORTED && page.backgroundSrc) {
          // eslint-disable-next-line no-await-in-loop
          await addImageToPdf(page.backgroundSrc, 0, 0, NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT);
        } else if (page.backgroundType === NOTEBOOK_BACKGROUNDS.GRID) {
          pdf.setDrawColor(219, 226, 235);
          pdf.setLineWidth(1);
          for (let x = 0; x <= NOTEBOOK_PAGE_WIDTH; x += 24) pdf.line(x, 0, x, NOTEBOOK_PAGE_HEIGHT);
          for (let y = 0; y <= NOTEBOOK_PAGE_HEIGHT; y += 24) pdf.line(0, y, NOTEBOOK_PAGE_WIDTH, y);
        } else if (page.backgroundType === NOTEBOOK_BACKGROUNDS.LINED) {
          pdf.setDrawColor(215, 225, 238);
          pdf.setLineWidth(1);
          for (let y = 88; y <= NOTEBOOK_PAGE_HEIGHT - 36; y += 32) {
            pdf.line(56, y, NOTEBOOK_PAGE_WIDTH - 48, y);
          }
          pdf.setDrawColor(252, 165, 165);
          pdf.line(72, 0, 72, NOTEBOOK_PAGE_HEIGHT);
        }

        if (page.backgroundType === NOTEBOOK_BACKGROUNDS.DOCUMENT) {
          const marginX = 84;
          const marginTop = 90;
          const maxTextWidth = NOTEBOOK_PAGE_WIDTH - marginX * 2;
          let cursorY = marginTop;
          const setTextColor = (value) => {
            const color = String(value || '').trim();
            const hex = color.match(/^#?([0-9a-f]{6})$/i);
            const rgb = color.match(/^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/i);
            if (hex) {
              const raw = hex[1];
              pdf.setTextColor(parseInt(raw.slice(0, 2), 16), parseInt(raw.slice(2, 4), 16), parseInt(raw.slice(4, 6), 16));
              return;
            }
            if (rgb) {
              pdf.setTextColor(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
              return;
            }
            pdf.setTextColor(31, 41, 55);
          };
          const blocks = parseDocumentBlocksForPdf(page.documentHtml || plainTextToDocumentHtml(page.documentContent || ''));
          blocks.forEach((block) => {
            const style = block.style || {};
            const fontSize = Math.max(10, Math.min(40, parseInt(style.fontSize, 10) || 17));
            const lineHeight = fontSize * 1.45;
            const family = /times|georgia/i.test(style.fontFamily || '') ? 'times' : (/courier/i.test(style.fontFamily || '') ? 'courier' : 'helvetica');
            const variant = style.bold && style.italic ? 'bolditalic' : (style.bold ? 'bold' : (style.italic ? 'italic' : 'normal'));
            pdf.setFont(family, variant);
            pdf.setFontSize(fontSize);
            setTextColor(style.color);
            const lines = pdf.splitTextToSize(block.text || ' ', maxTextWidth);
            lines.forEach((line) => {
              if (cursorY <= NOTEBOOK_PAGE_HEIGHT - 76) {
                const lineWidth = pdf.getTextWidth(line);
                const x = style.align === 'center'
                  ? (NOTEBOOK_PAGE_WIDTH - lineWidth) / 2
                  : (style.align === 'right' ? NOTEBOOK_PAGE_WIDTH - marginX - lineWidth : marginX);
                pdf.text(line, x, cursorY);
                if (style.underline) {
                  pdf.setDrawColor(31, 41, 55);
                  pdf.setLineWidth(1);
                  pdf.line(x, cursorY + 2, x + lineWidth, cursorY + 2);
                }
              }
              cursorY += lineHeight;
            });
            cursorY += 8;
          });
          pdf.setFont('helvetica', 'normal');
        }

        const pagePaths = paths.filter((path) => belongsToNotebookPage(path, page.id) && path.tool !== ToolType.ERASER);
        pagePaths.forEach((path) => {
          const points = path.points || [];
          if (points.length < 2) return;
          pdf.setDrawColor(path.color || '#111827');
          pdf.setLineWidth(path.width || 2);
          for (let p = 1; p < points.length; p += 1) {
            pdf.line(toPageX(points[p - 1].x), toPageY(points[p - 1].y), toPageX(points[p].x), toPageY(points[p].y));
          }
        });

        shapes.filter((shape) => belongsToNotebookPage(shape, page.id)).forEach((shape) => {
          pdf.setDrawColor(shape.color || '#111827');
          pdf.setLineWidth(shape.strokeWidth || 2);
          const x = toPageX(Math.min(shape.x1, shape.x2));
          const y = toPageY(Math.min(shape.y1, shape.y2));
          const width = Math.abs(shape.x2 - shape.x1);
          const height = Math.abs(shape.y2 - shape.y1);
          if (shape.type === ShapeType.RECTANGLE) pdf.rect(x, y, width, height);
          if (shape.type === ShapeType.CIRCLE) pdf.ellipse(toPageX((shape.x1 + shape.x2) / 2), toPageY((shape.y1 + shape.y2) / 2), width / 2, height / 2);
          if (shape.type === ShapeType.LINE || shape.type === ShapeType.ARROW) pdf.line(toPageX(shape.x1), toPageY(shape.y1), toPageX(shape.x2), toPageY(shape.y2));
          if (shape.type === ShapeType.TRIANGLE) {
            const vertices = getTriangleVertices(shape);
            pdf.triangle(toPageX(vertices[0].x), toPageY(vertices[0].y), toPageX(vertices[1].x), toPageY(vertices[1].y), toPageX(vertices[2].x), toPageY(vertices[2].y));
          }
        });

        const pageElements = elements.filter((element) => belongsToNotebookPage(element, page.id));
        for (const element of pageElements) {
          const elementX = toPageX(element.x);
          const elementY = toPageY(element.y);
          if (element.type === ElementType.WORKSHEET && element.src) {
            // eslint-disable-next-line no-await-in-loop
            await addImageToPdf(element.src, elementX, elementY, element.width || 300, element.height || 400);
          }
          if (element.type === ElementType.NOTE || element.type === ElementType.TEXT) {
            if (element.type === ElementType.NOTE) {
              pdf.setFillColor(element.color || '#fff9c4');
              pdf.rect(elementX, elementY, element.width || 240, element.height || 160, 'F');
            }
            pdf.setTextColor(51, 65, 85);
            pdf.setFontSize(element.type === ElementType.TEXT ? 16 : 13);
            const text = String(element.content || '');
            const lines = pdf.splitTextToSize(text, Math.max(40, (element.width || 240) - 24));
            pdf.text(lines, elementX + 12, elementY + 24);
          }
          if (element.type === ElementType.MATH || element.type === ElementType.CHART || element.type === ElementType.FBD) {
            pdf.setDrawColor(203, 213, 225);
            pdf.setFillColor(248, 250, 252);
            pdf.rect(elementX, elementY, element.width || 280, element.height || 120, 'FD');
            pdf.setTextColor(51, 65, 85);
            pdf.setFontSize(13);
            const text = element.type === ElementType.MATH
              ? (element.expression || element.rawInput || 'Math')
              : (element.type === ElementType.CHART
                ? normalizeGraphExpressions(element.expressions, 'Graph')
                : (element.bodyLabel || 'Free body diagram'));
            const lines = pdf.splitTextToSize(String(text), Math.max(40, (element.width || 280) - 24));
            pdf.text(lines, elementX + 12, elementY + 24);
          }
        }
      }

      return pdf;
    }

    const canvasArea = canvasAreaRef.current;
    if (!canvasArea) return null;

    try {
      // Use html2canvas to capture the entire canvas area including elements
      const capturedCanvas = await html2canvas(canvasArea, {
        backgroundColor: '#f8f9fa',
        scale: 2, // Higher resolution
        useCORS: true,
        logging: false,
      });

      // Create PDF
      const imgData = capturedCanvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: capturedCanvas.width > capturedCanvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [capturedCanvas.width, capturedCanvas.height]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, capturedCanvas.width, capturedCanvas.height);
      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    }
  }, [boardMode, elements, notebook, paths, shapes]);

  // Expose methods for parent component - must be after all handlers are defined
  useEffect(() => {
    apiRef.current = {
      getSnapshot: () => ({
        version: SNAPSHOT_VERSION,
        mode: boardMode,
        notebook: normalizeNotebook(notebook),
        paths: normalizePaths(paths).filter((path) => path.tool !== ToolType.ERASER),
        elements,
        shapes,
        uploads,
        viewport: { zoom, panX, panY },
        _tombstones: tombstones,
      }),
      exportToSvg,
      exportToPng,
      exportToPdf,
      canvasAreaRef,
      // Expose element creation methods for content generation
      addNote: (content, color) => {
        const safeContent = sanitizeOptionalText(content);
        const safeColor = sanitizeOptionalText(color).toLowerCase();
        const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.NOTE].width, ELEMENT_DIMENSIONS[ElementType.NOTE].height);
        const newNote = stampUpdatedMeta(withNotebookPage(
          createNoteElement(pos.x, pos.y),
          isNotebookMode ? activeNotebookPageId : null
        ));
        if (safeContent) newNote.content = safeContent;
        if (safeColor) {
          const colorMap = {
            'yellow': NOTE_COLORS[0],
            'pink': NOTE_COLORS[1],
            'blue': NOTE_COLORS[2],
            'green': NOTE_COLORS[3],
            'purple': NOTE_COLORS[4]
          };
          newNote.color = colorMap[safeColor] || NOTE_COLORS[0];
        }
        markDiscreteChange();
        setElements(prev => [...prev, newNote]);
        return newNote;
      },
      addText: (content) => {
        const safeContent = sanitizeOptionalText(content);
        const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.TEXT].width, ELEMENT_DIMENSIONS[ElementType.TEXT].height);
        const newText = stampUpdatedMeta(withNotebookPage(
          createTextElement(pos.x, pos.y),
          isNotebookMode ? activeNotebookPageId : null
        ));
        if (safeContent) newText.content = safeContent;
        markDiscreteChange();
        setElements(prev => [...prev, newText]);
        return newText;
      },
      addMath: (expression) => {
        const safeExpression = sanitizeOptionalText(expression);
        const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.MATH].width, ELEMENT_DIMENSIONS[ElementType.MATH].height);
        const newMath = stampUpdatedMeta(withNotebookPage(
          createMathElement(pos.x, pos.y),
          isNotebookMode ? activeNotebookPageId : null
        ));
        if (safeExpression) {
          newMath.expression = safeExpression;
          newMath.rawInput = safeExpression;
          newMath.isEditing = false;
        }
        markDiscreteChange();
        setElements(prev => [...prev, newMath]);
        return newMath;
      },
      addChart: (expression) => {
        const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.CHART].width, ELEMENT_DIMENSIONS[ElementType.CHART].height);
        const newChart = stampUpdatedMeta(withNotebookPage(
          createChartElement(pos.x, pos.y),
          isNotebookMode ? activeNotebookPageId : null
        ));
        newChart.expressions = normalizeGraphExpressions(expression, DEFAULT_GRAPH_EXPRESSION);
        markDiscreteChange();
        setElements(prev => [...prev, newChart]);
        return newChart;
      },
      addFbd: () => {
        const pos = findEmptyPosition(ELEMENT_DIMENSIONS[ElementType.FBD].width, ELEMENT_DIMENSIONS[ElementType.FBD].height);
        const newFbd = stampUpdatedMeta(withNotebookPage(
          createFbdElement(pos.x, pos.y),
          isNotebookMode ? activeNotebookPageId : null
        ));
        markDiscreteChange();
        setElements(prev => [...prev, newFbd]);
        return newFbd;
      }
    };

    // Notify parent when API is ready/updated
    if (onMount) {
      onMount(apiRef.current);
    }
  }, [activeNotebookPageId, boardMode, elements, exportToPdf, exportToPng, exportToSvg, findEmptyPosition, isNotebookMode, notebook, onMount, panX, panY, paths, shapes, stampUpdatedMeta, tombstones, uploads, zoom, markDiscreteChange]);

  const handleToggleElementAnnotating = useCallback((id) => {
    setActiveElementAnnotationId((prev) => (prev === id ? null : id));
    setSelectedElementIds([id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
  }, []);

  // Render element based on type
  const renderElement = useCallback((el, isSelected = false) => (
    <WhiteboardElementContent
      element={el}
      isSelected={isSelected}
      updateElement={updateElement}
      currentTool={currentTool}
      currentColor={currentColor}
      focusedTextElementId={focusedTextElementId}
      isAnnotating={activeElementAnnotationId === el.id}
      onToggleAnnotating={handleToggleElementAnnotating}
    />
  ), [activeElementAnnotationId, currentColor, currentTool, focusedTextElementId, handleToggleElementAnnotating, updateElement]);

  const formatElementAuthor = useCallback((updatedBy) => {
    const value = String(updatedBy || '').trim();
    if (!value) return '';
    if (value === syncClientId) return 'You';
    return `By ${value.slice(0, 8)}`;
  }, [syncClientId]);

  const handleElementSelect = useCallback((id, event) => {
    const isShift = !!event?.shiftKey;
    if (isShift) {
      setSelectedElementIds((prev) => (
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      ));
      return;
    }
    if (activeElementAnnotationId && activeElementAnnotationId !== id) {
      setActiveElementAnnotationId(null);
    }
    setSelectedElementIds([id]);
    setSelectedShapeIds([]);
    setSelectedPathIds([]);
  }, [activeElementAnnotationId]);

  const handleElementInteractionStart = useCallback(() => {
    elementInteractionDepthRef.current += 1;
    suppressHistoryRef.current = true;
    beginLocalInteraction();
  }, [beginLocalInteraction]);

  const handleElementInteractionEnd = useCallback(({ didMutate } = {}) => {
    elementInteractionDepthRef.current = Math.max(0, elementInteractionDepthRef.current - 1);
    if (elementInteractionDepthRef.current === 0) {
      suppressHistoryRef.current = false;
      if (didMutate) {
        window.requestAnimationFrame(() => {
          pushHistorySnapshot();
        });
      }
      endLocalInteraction(Boolean(didMutate));
    }
  }, [endLocalInteraction, pushHistorySnapshot]);

  const handleSelectionMoveStart = useCallback((e) => {
    if (!selectedBounds || currentTool !== ToolType.SELECT) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = getAdjustedPos(e, canvasRef.current);
    setIsMovingSelection(true);
    suppressHistoryRef.current = true;
    beginLocalInteraction();
    selectionDragRef.current = {
      mode: 'move',
      startX: pos.x,
      startY: pos.y,
      didMutate: false,
      elementIds: [...selectedElementIds],
      shapeIds: [...selectedShapeIds],
      pathIds: [...selectedPathIds],
      elements,
      shapes,
      paths
    };
  }, [beginLocalInteraction, currentTool, elements, getAdjustedPos, paths, selectedBounds, selectedElementIds, selectedPathIds, selectedShapeIds, shapes]);

  const handleSelectionResizeStart = useCallback((e) => {
    if (!selectedBounds || currentTool !== ToolType.SELECT) return;
    const singleShapeId = selectedShapeIds.length === 1 && !selectedElementIds.length && !selectedPathIds.length
      ? selectedShapeIds[0]
      : null;
    const pathOnlySelection = selectedPathIds.length > 0 && !selectedElementIds.length && !selectedShapeIds.length;
    const selectedPathSet = new Set(selectedPathIds);
    const pathOnlyResizable = pathOnlySelection && paths
      .filter((path) => selectedPathSet.has(path.id))
      .every(isPathGroupResizable);
    const singlePathId = pathOnlyResizable && selectedPathIds.length === 1
      ? selectedPathIds[0]
      : null;
    const groupedPathBounds = pathOnlyResizable
      ? getGroupedPathBounds(selectedPathIds, paths)
      : null;
    if (!singleShapeId && !singlePathId && !pathOnlyResizable) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizingSelection(true);
    suppressHistoryRef.current = true;
    beginLocalInteraction();
    selectionDragRef.current = {
      mode: 'resize',
      bounds: (pathOnlyResizable && groupedPathBounds) ? groupedPathBounds : selectedBounds,
      targetType: singleShapeId ? 'shape' : (singlePathId ? 'path' : 'path-group'),
      targetId: singleShapeId || singlePathId || null,
      targetIds: !singleShapeId && !singlePathId ? [...selectedPathIds] : null,
      didMutate: false,
      shapes,
      paths
    };
  }, [beginLocalInteraction, currentTool, paths, selectedBounds, selectedElementIds.length, selectedPathIds, selectedShapeIds, shapes]);

  const handleSelectionRotateStart = useCallback((e) => {
    if (!selectedBounds || currentTool !== ToolType.SELECT || !canRotateSelection) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = getAdjustedPos(e, canvasRef.current);
    const center = {
      x: (selectedBounds.minX + selectedBounds.maxX) / 2,
      y: (selectedBounds.minY + selectedBounds.maxY) / 2,
    };
    setIsRotatingSelection(true);
    suppressHistoryRef.current = true;
    beginLocalInteraction();
    selectionDragRef.current = {
      mode: 'rotate',
      center,
      startAngle: Math.atan2(pos.y - center.y, pos.x - center.x),
      didMutate: false,
      shapeIds: [...selectedShapeIds],
      pathIds: [...selectedPathIds],
      shapes,
      paths,
    };
  }, [beginLocalInteraction, canRotateSelection, currentTool, getAdjustedPos, paths, selectedBounds, selectedPathIds, selectedShapeIds, shapes]);

  const gridStyle = useMemo(() => {
    const spacing = Math.max(
      GRID_MIN_SPACING,
      Math.min(GRID_MAX_SPACING, GRID_BASE_SPACING * zoom)
    );
    return {
      backgroundSize: `${spacing}px ${spacing}px`,
      backgroundPosition: `${panX}px ${panY}px`
    };
  }, [panX, panY, zoom]);

  const minimapData = useMemo(() => {
    if (!canvasSize.width || !canvasSize.height) return null;

    const baseBounds = computeContentBounds();
    const viewportWorldX = -panX / zoom;
    const viewportWorldY = -panY / zoom;
    const viewportWorldWidth = canvasSize.width / zoom;
    const viewportWorldHeight = canvasSize.height / zoom;

    const bounds = {
      minX: Math.min(baseBounds.minX, viewportWorldX) - 80,
      minY: Math.min(baseBounds.minY, viewportWorldY) - 80,
      maxX: Math.max(baseBounds.maxX, viewportWorldX + viewportWorldWidth) + 80,
      maxY: Math.max(baseBounds.maxY, viewportWorldY + viewportWorldHeight) + 80
    };

    const worldWidth = Math.max(1, bounds.maxX - bounds.minX);
    const worldHeight = Math.max(1, bounds.maxY - bounds.minY);
    const innerWidth = MINIMAP_WIDTH - MINIMAP_PADDING * 2;
    const innerHeight = MINIMAP_HEIGHT - MINIMAP_PADDING * 2;
    const scale = Math.min(innerWidth / worldWidth, innerHeight / worldHeight);
    const contentWidth = worldWidth * scale;
    const contentHeight = worldHeight * scale;
    const offsetX = (MINIMAP_WIDTH - contentWidth) / 2;
    const offsetY = (MINIMAP_HEIGHT - contentHeight) / 2;

    const viewportX = offsetX + (viewportWorldX - bounds.minX) * scale;
    const viewportY = offsetY + (viewportWorldY - bounds.minY) * scale;
    const viewportWidth = Math.max(10, viewportWorldWidth * scale);
    const viewportHeight = Math.max(10, viewportWorldHeight * scale);

    return {
      bounds,
      scale,
      offsetX,
      offsetY,
      contentWidth,
      contentHeight,
      viewportX,
      viewportY,
      viewportWidth,
      viewportHeight
    };
  }, [canvasSize.height, canvasSize.width, computeContentBounds, panX, panY, zoom]);

  const worldToMinimap = useCallback((x, y, data) => {
    return {
      x: data.offsetX + (x - data.bounds.minX) * data.scale,
      y: data.offsetY + (y - data.bounds.minY) * data.scale
    };
  }, []);

  useEffect(() => {
    const canvas = minimapCanvasRef.current;
    if (!canvas || !minimapData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (minimapDrawRafRef.current) {
      cancelAnimationFrame(minimapDrawRafRef.current);
    }

    minimapDrawRafRef.current = window.requestAnimationFrame(() => {

    ctx.clearRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Background + subtle graph tint
    ctx.fillStyle = 'rgba(248, 250, 252, 0.96)';
    ctx.fillRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    ctx.fillStyle = 'rgba(226, 232, 240, 0.55)';
    ctx.fillRect(
      minimapData.offsetX,
      minimapData.offsetY,
      minimapData.contentWidth,
      minimapData.contentHeight
    );

    // Mini grid
    const miniGrid = Math.max(6, Math.min(18, 24 * minimapData.scale));
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.28)';
    ctx.lineWidth = 1;
    for (let x = minimapData.offsetX; x <= minimapData.offsetX + minimapData.contentWidth; x += miniGrid) {
      ctx.beginPath();
      ctx.moveTo(x, minimapData.offsetY);
      ctx.lineTo(x, minimapData.offsetY + minimapData.contentHeight);
      ctx.stroke();
    }
    for (let y = minimapData.offsetY; y <= minimapData.offsetY + minimapData.contentHeight; y += miniGrid) {
      ctx.beginPath();
      ctx.moveTo(minimapData.offsetX, y);
      ctx.lineTo(minimapData.offsetX + minimapData.contentWidth, y);
      ctx.stroke();
    }

    // Paths
    const pathsToRender = activeStrokeRef.current ? [...visiblePaths, activeStrokeRef.current] : visiblePaths;
    pathsToRender.forEach(path => {
      const points = path.points || [];
      if (points.length < 2) return;
      ctx.beginPath();
      const first = worldToMinimap(points[0].x, points[0].y, minimapData);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < points.length; i += 1) {
        const p = worldToMinimap(points[i].x, points[i].y, minimapData);
        ctx.lineTo(p.x, p.y);
      }
      ctx.strokeStyle = path.tool === ToolType.ERASER ? 'rgba(248,250,252,0.95)' : (path.color || '#334155');
      ctx.lineWidth = Math.max(1, (path.width || 2) * minimapData.scale * 0.55);
      ctx.globalAlpha = path.tool === ToolType.MARKER ? 0.5 : 0.9;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    // Shapes
    const shapesToRender = currentShape ? [...visibleShapes, currentShape] : visibleShapes;
    shapesToRender.forEach(shape => {
      const p1 = worldToMinimap(shape.x1, shape.y1, minimapData);
      const p2 = worldToMinimap(shape.x2, shape.y2, minimapData);
      ctx.strokeStyle = shape.color || '#475569';
      ctx.lineWidth = Math.max(1, (shape.strokeWidth || 2) * minimapData.scale * 0.6);
      ctx.globalAlpha = 0.9;

      if (shape.type === ShapeType.RECTANGLE) {
        ctx.strokeRect(
          Math.min(p1.x, p2.x),
          Math.min(p1.y, p2.y),
          Math.abs(p2.x - p1.x),
          Math.abs(p2.y - p1.y)
        );
      } else if (shape.type === ShapeType.CIRCLE) {
        ctx.beginPath();
        ctx.ellipse(
          (p1.x + p2.x) / 2,
          (p1.y + p2.y) / 2,
          Math.abs(p2.x - p1.x) / 2,
          Math.abs(p2.y - p1.y) / 2,
          0,
          0,
          Math.PI * 2
        );
        ctx.stroke();
      } else if (shape.type === ShapeType.TRIANGLE) {
        const vertices = getTriangleVertices(shape).map((point) => worldToMinimap(point.x, point.y, minimapData));
        ctx.beginPath();
        ctx.moveTo(vertices[0].x, vertices[0].y);
        ctx.lineTo(vertices[1].x, vertices[1].y);
        ctx.lineTo(vertices[2].x, vertices[2].y);
        ctx.closePath();
        ctx.stroke();
      } else if (shape.type === ShapeType.LINE || shape.type === ShapeType.ARROW) {
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    });

    // Elements (notes/math/graph) with type cues
    visibleElements.forEach(el => {
      const width = el.width || ELEMENT_DIMENSIONS[el.type]?.width || 120;
      const height = el.height || ELEMENT_DIMENSIONS[el.type]?.height || 80;
      const topLeft = worldToMinimap(el.x, el.y, minimapData);
      const miniW = Math.max(4, width * minimapData.scale);
      const miniH = Math.max(4, height * minimapData.scale);

      if (el.type === ElementType.NOTE) {
        ctx.fillStyle = 'rgba(253, 224, 71, 0.62)';
        ctx.strokeStyle = 'rgba(202, 138, 4, 0.75)';
      } else if (el.type === ElementType.MATH) {
        ctx.fillStyle = 'rgba(191, 219, 254, 0.65)';
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.85)';
      } else if (el.type === ElementType.WORKSHEET) {
        ctx.fillStyle = 'rgba(203, 213, 225, 0.75)';
        ctx.strokeStyle = 'rgba(71, 85, 105, 0.86)';
      } else if (el.type === ElementType.FBD) {
        ctx.fillStyle = 'rgba(219, 234, 254, 0.64)';
        ctx.strokeStyle = 'rgba(37, 99, 235, 0.84)';
      } else {
        ctx.fillStyle = 'rgba(196, 181, 253, 0.62)';
        ctx.strokeStyle = 'rgba(124, 58, 237, 0.82)';
      }

      ctx.lineWidth = 1;
      ctx.fillRect(topLeft.x, topLeft.y, miniW, miniH);
      ctx.strokeRect(topLeft.x, topLeft.y, miniW, miniH);

      // Type glyph
      ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
      ctx.font = 'bold 9px sans-serif';
      const glyph = el.type === ElementType.NOTE ? 'N' : (el.type === ElementType.MATH ? 'M' : (el.type === ElementType.TEXT ? 'T' : (el.type === ElementType.WORKSHEET ? 'W' : (el.type === ElementType.FBD ? 'F' : 'G'))));
      ctx.fillText(glyph, topLeft.x + 2, topLeft.y + 10);

      if (miniW >= 34 && miniH >= 16) {
        ctx.font = '8px sans-serif';
        ctx.fillStyle = 'rgba(30, 41, 59, 0.7)';
        const previewRaw = el.type === ElementType.NOTE || el.type === ElementType.TEXT
          ? (el.content || '')
          : (el.type === ElementType.MATH
            ? (el.expression || el.rawInput || '')
            : (el.type === ElementType.WORKSHEET
              ? (el.pdfMeta?.filename || 'worksheet')
              : (el.type === ElementType.FBD
                ? (el.bodyLabel || 'FBD')
                : normalizeGraphExpressions(el.expressions, 'graph'))));
        const firstLine = previewRaw.toString().split('\n')[0].trim();
        const preview = firstLine.slice(0, 18);
        if (preview) {
          ctx.fillText(preview, topLeft.x + 12, topLeft.y + 10);
        }
      }
    });

    // Outer content frame
    ctx.strokeStyle = 'rgba(71, 85, 105, 0.55)';
    ctx.lineWidth = 1;
    ctx.strokeRect(
      minimapData.offsetX + 0.5,
      minimapData.offsetY + 0.5,
      Math.max(0, minimapData.contentWidth - 1),
      Math.max(0, minimapData.contentHeight - 1)
    );
    });

    return () => {
      if (minimapDrawRafRef.current) {
        cancelAnimationFrame(minimapDrawRafRef.current);
        minimapDrawRafRef.current = null;
      }
    };
  }, [currentShape, minimapData, visibleElements, visiblePaths, visibleShapes, worldToMinimap]);

  const recenterToWorldPoint = useCallback((worldX, worldY) => {
    const targetPan = clampPan(
      (canvasSize.width / 2) - worldX * zoom,
      (canvasSize.height / 2) - worldY * zoom
    );
    setPanX(targetPan.x);
    setPanY(targetPan.y);
  }, [canvasSize.height, canvasSize.width, clampPan, zoom]);

  const handleMinimapPointerDown = useCallback((e) => {
    if (!minimapData) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const insideViewport =
      x >= minimapData.viewportX &&
      x <= minimapData.viewportX + minimapData.viewportWidth &&
      y >= minimapData.viewportY &&
      y <= minimapData.viewportY + minimapData.viewportHeight;

    if (insideViewport) {
      minimapDragRef.current = {
        active: true,
        pointerId: e.pointerId,
        offsetX: x - minimapData.viewportX,
        offsetY: y - minimapData.viewportY,
        startX: x,
        startY: y,
        dragged: false
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    const clampedX = Math.min(Math.max(x, minimapData.offsetX), minimapData.offsetX + minimapData.contentWidth);
    const clampedY = Math.min(Math.max(y, minimapData.offsetY), minimapData.offsetY + minimapData.contentHeight);
    const worldX = minimapData.bounds.minX + (clampedX - minimapData.offsetX) / minimapData.scale;
    const worldY = minimapData.bounds.minY + (clampedY - minimapData.offsetY) / minimapData.scale;
    recenterToWorldPoint(worldX, worldY);
  }, [minimapData, recenterToWorldPoint]);

  const handleMinimapPointerMove = useCallback((e) => {
    if (!minimapData) return;
    if (!minimapDragRef.current.active || minimapDragRef.current.pointerId !== e.pointerId) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;
    if (
      Math.abs(rawX - minimapDragRef.current.startX) > 2 ||
      Math.abs(rawY - minimapDragRef.current.startY) > 2
    ) {
      minimapDragRef.current.dragged = true;
    }

    const x = e.clientX - rect.left - minimapDragRef.current.offsetX + (minimapData.viewportWidth / 2);
    const y = e.clientY - rect.top - minimapDragRef.current.offsetY + (minimapData.viewportHeight / 2);

    const clampedX = Math.min(Math.max(x, minimapData.offsetX), minimapData.offsetX + minimapData.contentWidth);
    const clampedY = Math.min(Math.max(y, minimapData.offsetY), minimapData.offsetY + minimapData.contentHeight);
    const worldX = minimapData.bounds.minX + (clampedX - minimapData.offsetX) / minimapData.scale;
    const worldY = minimapData.bounds.minY + (clampedY - minimapData.offsetY) / minimapData.scale;

    recenterToWorldPoint(worldX, worldY);
  }, [minimapData, recenterToWorldPoint]);

  const handleMinimapPointerUp = useCallback((e) => {
    if (minimapDragRef.current.pointerId === e.pointerId) {
      const { active, dragged, startX, startY } = minimapDragRef.current;
      if (active && !dragged && minimapData) {
        const x = startX + minimapData.viewportWidth / 2;
        const y = startY + minimapData.viewportHeight / 2;
        const clampedX = Math.min(Math.max(x, minimapData.offsetX), minimapData.offsetX + minimapData.contentWidth);
        const clampedY = Math.min(Math.max(y, minimapData.offsetY), minimapData.offsetY + minimapData.contentHeight);
        const worldX = minimapData.bounds.minX + (clampedX - minimapData.offsetX) / minimapData.scale;
        const worldY = minimapData.bounds.minY + (clampedY - minimapData.offsetY) / minimapData.scale;
        recenterToWorldPoint(worldX, worldY);
      }

      minimapDragRef.current = {
        active: false,
        pointerId: null,
        offsetX: 0,
        offsetY: 0,
        startX: 0,
        startY: 0,
        dragged: false
      };
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
  }, [minimapData, recenterToWorldPoint]);

  return (
    <div className="wb-canvas-container" ref={containerRef}>
      {/* Toolbar */}
      <Toolbar
        currentTool={currentTool}
        currentColor={currentColor}
        currentShapeType={currentShapeType}
        eraserSize={eraserSize}
        eraserMode={eraserMode}
        boardMode={boardMode}
        onModeChange={handleModeChange}
        setTool={setCurrentTool}
        setColor={setCurrentColor}
        setShapeType={setCurrentShapeType}
        setEraserSize={setEraserSize}
        setEraserMode={setEraserMode}
        onAddNote={handleAddNote}
        onAddText={handleAddText}
        onAddMath={handleAddMath}
        onAddChart={handleAddChart}
        onAddFbd={handleAddFbd}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onClear={handleClear}
      />

      {isNotebookMode && (
        <div className="wb-notebook-controls">
          <button
            type="button"
            onClick={() => {
              const current = normalizeNotebook(notebook);
              const index = current.pages.findIndex((page) => page.id === current.activePageId);
              const prevPage = current.pages[Math.max(0, index - 1)];
              if (prevPage) handleNotebookPageChange(prevPage.id);
            }}
            disabled={normalizeNotebook(notebook).pages[0]?.id === activeNotebookPageId}
          >
            Prev
          </button>
          <select
            value={activeNotebookPageId || ''}
            onChange={(event) => handleNotebookPageChange(event.target.value)}
            aria-label="Notebook page"
          >
            {normalizeNotebook(notebook).pages.map((page, index) => (
              <option key={page.id} value={page.id}>
                {index + 1}. {page.title || `Page ${index + 1}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              const current = normalizeNotebook(notebook);
              const index = current.pages.findIndex((page) => page.id === current.activePageId);
              const nextPage = current.pages[Math.min(current.pages.length - 1, index + 1)];
              if (nextPage) handleNotebookPageChange(nextPage.id);
            }}
            disabled={normalizeNotebook(notebook).pages[normalizeNotebook(notebook).pages.length - 1]?.id === activeNotebookPageId}
          >
            Next
          </button>
          <button type="button" onClick={handleAddNotebookPage}>Add Page</button>
          <button type="button" onClick={handleDuplicateNotebookPage}>Duplicate</button>
          <button type="button" onClick={handleDeleteNotebookPage} disabled={normalizeNotebook(notebook).pages.length <= 1}>Delete</button>
          <select
            value={activeNotebookPage?.backgroundType || NOTEBOOK_BACKGROUNDS.LINED}
            onChange={(event) => handleNotebookBackgroundChange(event.target.value)}
            aria-label="Notebook page background"
          >
            <option value={NOTEBOOK_BACKGROUNDS.LINED}>Lined</option>
            <option value={NOTEBOOK_BACKGROUNDS.DOCUMENT}>Document</option>
            <option value={NOTEBOOK_BACKGROUNDS.BLANK}>Blank</option>
            <option value={NOTEBOOK_BACKGROUNDS.GRID}>Grid</option>
            {activeNotebookPage?.backgroundSrc && (
              <option value={NOTEBOOK_BACKGROUNDS.IMPORTED}>Imported</option>
            )}
          </select>
        </div>
      )}

      {/* Main canvas area */}
      <div
        className="wb-canvas-area"
        ref={canvasAreaRef}
        onWheel={handleWheel}
        onPointerDown={handleStart}
        onPointerMove={handleMove}
        onPointerUp={handleEnd}
        onPointerCancel={handleEnd}
        style={{ cursor: isPanning ? 'grabbing' : (isMovingSelection ? 'move' : ((spacePressed || currentTool === ToolType.PAN) ? 'grab' : currentTool === ToolType.SELECT ? 'default' : currentTool === ToolType.TEXT ? 'text' : 'crosshair')) }}
      >
        {/* Grid Background */}
        {!isNotebookMode && (
          <div
            className="wb-grid-background"
            style={gridStyle}
          />
        )}

        {isNotebookMode && notebookPageRects.map((rect) => (
          <div
            key={rect.pageId}
            className={`wb-notebook-page wb-notebook-page--${rect.page.backgroundType || NOTEBOOK_BACKGROUNDS.LINED} ${rect.pageId === activeNotebookPageId ? 'is-active' : ''}`}
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
              transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
              transformOrigin: '0 0',
            }}
          >
            {rect.page.backgroundType === NOTEBOOK_BACKGROUNDS.IMPORTED && rect.page.backgroundSrc && (
              <img src={rect.page.backgroundSrc} alt="" className="wb-notebook-page__background" draggable={false} />
            )}
            {rect.page.backgroundType === NOTEBOOK_BACKGROUNDS.DOCUMENT && (
              <NotebookDocumentPage
                html={rect.page.documentHtml || plainTextToDocumentHtml(rect.page.documentContent || '')}
                onChange={(html) => handleNotebookDocumentChange(rect.pageId, html)}
                readOnly={currentTool !== ToolType.SELECT}
                isActive={rect.pageId === activeNotebookPageId}
              />
            )}
          </div>
        ))}

        {/* Drawing Canvas */}
        <canvas
          ref={canvasRef}
          className={`wb-drawing-canvas ${currentTool === ToolType.SELECT ? 'pointer-events-none' : ''}`}
          onPointerDown={handleStart}
          onPointerMove={handleMove}
          onPointerUp={handleEnd}
          onPointerCancel={handleEnd}
        />

        {/* Elements Layer - with zoom/pan transform */}
        <div
          className="wb-elements-layer"
          style={{
            transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
            transformOrigin: '0 0'
          }}
        >
          {visibleElements.map(el => (
            <DraggableItem
              key={el.id}
              id={el.id}
              x={el.x}
              y={el.y}
              isSelected={selectedElementIds.includes(el.id)}
              onSelect={handleElementSelect}
              onMove={moveElement}
              onResize={resizeElement}
              onDelete={deleteElement}
              clientToWorld={clientToWorld}
              width={el.width}
              height={el.height}
              minWidth={ELEMENT_DIMENSIONS[el.type]?.minWidth || 120}
              minHeight={ELEMENT_DIMENSIONS[el.type]?.minHeight || 100}
              headerColor={el.type === ElementType.NOTE ? '' : 'wb-header-white'}
              className={el.type === ElementType.TEXT ? 'wb-draggable-item--text' : ''}
              onInteractionStart={handleElementInteractionStart}
              onInteractionEnd={handleElementInteractionEnd}
              authorLabel={formatElementAuthor(el._updated_by)}
            >
              {renderElement(el, selectedElementIds.includes(el.id))}
            </DraggableItem>
          ))}

          {selectionBox && (
            <div
              className="wb-selection-marquee"
              style={{
                left: Math.min(selectionBox.startX, selectionBox.endX),
                top: Math.min(selectionBox.startY, selectionBox.endY),
                width: Math.abs(selectionBox.endX - selectionBox.startX),
                height: Math.abs(selectionBox.endY - selectionBox.startY),
              }}
            />
          )}

          {showSelectionFrame && (
            <div
              className="wb-selection-frame"
              style={{
                left: selectedBounds.minX,
                top: selectedBounds.minY,
                width: Math.max(2, selectedBounds.maxX - selectedBounds.minX),
                height: Math.max(2, selectedBounds.maxY - selectedBounds.minY),
              }}
              onPointerDown={handleSelectionMoveStart}
            >
              <button
                className="wb-selection-delete"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteSelection();
                }}
                title="Delete selection"
                aria-label="Delete selection"
              >
                X
              </button>
              {canResizeSelection && (
                <button
                  className="wb-selection-resize"
                  onPointerDown={handleSelectionResizeStart}
                  title="Resize selection"
                  aria-label="Resize selection"
                />
              )}
              {canRotateSelection && (
                <button
                  className="wb-selection-rotate"
                  onPointerDown={handleSelectionRotateStart}
                  title="Rotate selection"
                  aria-label="Rotate selection"
                />
              )}
            </div>
          )}

          {selectedShape && selectedBounds && (
            <div
              className="wb-shape-props"
              style={{ left: selectedBounds.minX, top: selectedBounds.maxY + 8 }}
            >
              {(selectedShape.type === ShapeType.ARROW || selectedShape.type === ShapeType.TRIANGLE) && (
                <button
                  className={`wb-shape-props-btn${selectedShape.showAngle ? ' active' : ''}`}
                  onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={toggleSelectedShapeAngles}
                  title={selectedShape.showAngle ? 'Hide angle labels' : 'Show angle labels'}
                >
                  {selectedShape.showAngle ? 'Hide angles' : 'Show angles'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="wb-remote-cursors" aria-hidden="true">
          {remoteCursors.map((cursor) => (
            <div
              key={cursor.clientId}
              className="wb-remote-cursor"
              style={{
                transform: `translate(${cursor.x * zoom + panX}px, ${cursor.y * zoom + panY}px)`,
              }}
            >
              <div className="wb-remote-cursor__pointer" />
              <div className="wb-remote-cursor__label">{cursor.displayLabel || 'Guest'}</div>
            </div>
          ))}
        </div>

        {!isPublicShare && (
          <WorksheetUploadTray
            expanded={uploadTrayExpanded}
            onToggleExpanded={() => setUploadTrayExpanded((prev) => !prev)}
            uploads={uploads}
            selectedAssetId={selectedUploadAssetId}
            selectedPdfPage={selectedUploadPage}
            onSelectAsset={(assetId) => {
              setSelectedUploadAssetId(assetId);
              setSelectedUploadPage(1);
            }}
            onChangePdfPage={setSelectedUploadPage}
            onPasteSelected={handlePasteSelectedUpload}
            onInsertAsPages={handleInsertUploadAsNotebookPages}
            onUploadFiles={handleUploadFiles}
            notebookMode={isNotebookMode}
            isProcessing={isProcessingUpload}
            uploadError={uploadError}
          />
        )}

        {minimapData && (
          <div
            className="wb-minimap"
            onPointerDown={handleMinimapPointerDown}
            onPointerMove={handleMinimapPointerMove}
            onPointerUp={handleMinimapPointerUp}
            onPointerCancel={handleMinimapPointerUp}
          >
            <canvas
              ref={minimapCanvasRef}
              className="wb-minimap__canvas"
              width={MINIMAP_WIDTH}
              height={MINIMAP_HEIGHT}
            />
            <div
              className="wb-minimap__viewport"
              style={{
                left: minimapData.viewportX,
                top: minimapData.viewportY,
                width: minimapData.viewportWidth,
                height: minimapData.viewportHeight
              }}
            />
          </div>
        )}

        {/* Floating Zoom Controls - Top Right */}
        <div className="wb-zoom-floating">
          <button
            onClick={() => setStylusOnlyMode((prev) => !prev)}
            className={`wb-zoom-btn ${stylusOnlyMode ? 'wb-zoom-btn-active' : ''}`}
            title={stylusOnlyMode ? 'Stylus-only draw enabled' : 'Stylus-only draw disabled'}
          >
            {stylusOnlyMode ? 'Pen' : 'Any'}
          </button>
          <button onClick={handleZoomOut} className="wb-zoom-btn" title="Zoom Out (-)">
            <ZoomOut size={16} />
          </button>
          <span className="wb-zoom-display">{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn} className="wb-zoom-btn" title="Zoom In (+)">
            <ZoomIn size={16} />
          </button>
          <button onClick={handleZoomReset} className="wb-zoom-btn" title="Reset View (0)">
            <Maximize size={16} />
          </button>
        </div>

      </div>

      {/* AI Input Bar */}
      {!isPublicShare && (
        <WhiteboardInput
          onAddNote={handleAddNote}
          onAddMath={handleAddMath}
          onAddChart={handleAddChart}
          onAddFbd={handleAddFbd}
          onAiMessage={handleAiMessage}
          injectedContext={injectedContext}
          onContextChange={onContextChange}
          currentPhase={currentPhase}
        />
      )}

      {/* AI Message Toast */}
      {aiMessage && (
        <div className="wb-ai-message-toast" onClick={() => { setAiMessage(null); setAiTechnique(null); }}>
          {aiTechnique && (
            <NeuroInformedBadge technique={aiTechnique} size="small" variant="subtle" showTooltip={false} />
          )}
          <span className="wb-ai-message-text">{aiMessage}</span>
        </div>
      )}
    </div>
  );
};

export default WhiteboardCanvas;
